import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarDays, Package, AlertCircle, CheckCircle2, Calculator, Info, Edit, Save, X, ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { bomHeaderApiService, BomHeader } from '../services/bomHeaderApi';
import { bomDetailApiService, BomDetail } from '../services/bomDetailApi';
import { grnApiService, GRN } from '../services/grnApi';
import { workOrderApiService, WorkOrder, WorkOrderStatus, WorkOrderPriority } from '../services/workOrderApi';
import { salesOrderApiService, SalesOrder as SalesOrderType } from '../services/salesOrderApi';
import { materialIssueApiService, MaterialIssueType, MaterialIssueStatus, MaterialIssueCreateRequest } from '../services/materialIssueApi';
import { workOrderOperationApiService, WorkOrderOperation, WorkOrderOperationStatus } from '../services/workOrderOperationApi';
import { rawItemVariantApiService, RawItemVariant } from '../services/rawItemVariantApi';
import { endgoodVariantApiService, EndgoodVariant } from '../services/endgoodVariantApi';
import { workOrderVariantBreakdownApiService, WorkOrderVariantBreakdownCreateRequest } from '../services/workOrderVariantBreakdownApi';
import { fileUploadApiService } from '../services/fileUploadApi';
import { processFlowApiService, ProcessFlow } from '../services/processFlowApi';
import { wipApiService, WorkInProgressSummary, WIPFilterRequest } from '../services/wipApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { getWorkOrderConfig } from '../config/uiConfig';

interface RawMaterial {
  id: number;
  grnId: number;
  grnNumber: string;
  heatNo: string;
  itemName: string;
  itemSku: string;
  receivedQuantity: number;
  remainingQuantity: number;
  unit: string;
  receivedDate: string;
  supplierName: string;
  rawItemId: number; // ID of the raw item this material belongs to
  nop?: number; // Total Number of Pieces
  remainingNop?: number; // Remaining Number of Pieces
  // For WIP (internal manufactured parts)
  isWIP?: boolean; // Flag to indicate if this is a WIP lot
  lotNo?: string; // Work order number (lot no) for WIP
  processId?: number; // Process ID for WIP
}

// Interface for GRN consumption response from BY_RAWITEM endpoint
interface GRNConsumptionDetail {
  heatNo: string;
  rawItemId: number;
  itemName: string;
  rawItemSku: string;
  receivedQuantity: number;
  originalQuantity: number;
  unitPrice: number;
  lineTotal: number | null;
  totalConsumed: number;
  remainingQuantity: number;
  issueCount: number;
  lastActivityDate: string;
  grnNumber: string;
  grnDate: string;
  supplierName: string;
}

interface SalesOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  startDate: string;
  endDate: string;
  quantity: number;
  availableQty: number;
  wipQty: number;
  pendingQty: number;
}

interface PartInfo {
  id: number;
  endgoodName: string;
  partNumber: string;
  sku: string;
  rmRequiredPerUnit: number; // Raw material required per unit
}

interface SelectedMaterial {
  materialId: number;
  rawMaterial: RawMaterial;
  issueQty: string;
}





export function WorkOrderForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  
  // Mode detection
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id || location.pathname.includes('/new');

  const [selectedSalesOrder, setSelectedSalesOrder] = useState<string>("");
  const [selectedPart, setSelectedPart] = useState<number | null>(null);
  const [selectedRawMaterial, setSelectedRawMaterial] = useState<number | null>(null); // Keep for backward compatibility
  const [selectedVariant, setSelectedVariant] = useState<number | null>(null);
  const [issueDate, setIssueDate] = useState<string>("");
  const [issueQty, setIssueQty] = useState<string>("");
  
  // Multiple material selection for process seq 1
  const [selectedMaterials, setSelectedMaterials] = useState<Map<number, SelectedMaterial>>(new Map());
  
  // Derived data
  const [currentSalesOrder, setCurrentSalesOrder] = useState<SalesOrder | null>(null);
  const [currentPart, setCurrentPart] = useState<PartInfo | null>(null);
  const [currentRawMaterial, setCurrentRawMaterial] = useState<RawMaterial | null>(null);
  const [calculatedParts, setCalculatedParts] = useState<number>(0);
  
  // API data
  const [parts, setParts] = useState<PartInfo[]>([]);
  const [salesOrders, setSalesOrders] = useState<SalesOrderType[]>([]);
  const [loading, setLoading] = useState(isEditMode || isViewMode);
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [loadingSalesOrderDetails, setLoadingSalesOrderDetails] = useState(false);

  const loadWorkOrderData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load sales orders first - filtered by selected branch
      if (selectedBranch) {
        const salesOrdersResponse = await salesOrderApiService.getSalesOrders(1, 1000, selectedBranch);
        if (salesOrdersResponse.success) {
          setSalesOrders(salesOrdersResponse.data || []);
        }
      }
      
      const response = await workOrderApiService.getWorkOrderById(id!);
      
      if (response.success && response.data) {
        const wo = response.data;
        setWorkOrder(wo);
        
        // Set selected values based on work order data
        // console.log('📋 Work Order Data:', wo);
        
        // In view mode, we use flattened fields from the response
        // Sales Order - use flattened salesOrderNumber
        if (isViewMode) {
          // For view mode, we'll display the sales order number directly
          // No need to set selectedSalesOrder since we're showing read-only
        } else {
          // For edit mode, set the sales order ID if available
          if (wo.salesOrder) {
            setSelectedSalesOrder(wo.salesOrder.id.toString());
          } else if (wo.salesOrderId) {
            setSelectedSalesOrder(wo.salesOrderId.toString());
          }
        }
        
        // Handle endgood - prioritize flattened fields, then nested structure
        const endgoodId = wo.endgoodId || wo.endgood?.id;
        const endgoodName = wo.endgoodName || wo.endgood?.endgoodName;
        const endgoodSku = wo.endgoodSku || wo.endgood?.sku;
        const endgoodPartNumber = wo.endgood?.partNumber;
        
        // console.log('🔍 Endgood Info:', { endgoodId, endgoodName, endgoodSku, endgoodPartNumber, endgood: wo.endgood });
        
        if (endgoodId) {
          setSelectedPart(endgoodId);
          // Populate parts array for view/edit mode so the endgood name can be displayed
          const partInfo: PartInfo = {
            id: endgoodId,
            endgoodName: endgoodName || `Endgood #${endgoodId}`,
            partNumber: endgoodPartNumber || endgoodSku || '',
            sku: endgoodSku || '',
            rmRequiredPerUnit: 1.0,
          };
          // console.log('✅ Setting part info:', partInfo);
          setParts([partInfo]);
        }
        
        // Handle BOM header - prioritize flattened fields, then nested structure
        const bomHeaderId = wo.bomHeaderId || wo.bomHeader?.id;
        const bomHeaderName = wo.bomName || wo.bomHeader?.bomName;
        const bomHeaderNumber = wo.bomNumber || wo.bomHeader?.bomNumber;
        const bomHeaderVersion = wo.bomHeader?.version;
        
        // console.log('🔍 BOM Header Info:', { bomHeaderId, bomHeaderName, bomHeaderNumber, bomHeaderVersion, bomHeader: wo.bomHeader });
        
        if (wo.bomHeader) {
          setCurrentBomHeader(wo.bomHeader);
          await loadBomDetails(wo.bomHeader.id);
        } else if (bomHeaderId) {
          // If only ID is available, fetch the full BOM header
          try {
            const bomHeader = await bomHeaderApiService.getBomHeaderById(bomHeaderId.toString());
            setCurrentBomHeader(bomHeader);
            await loadBomDetails(bomHeader.id);
          } catch (error) {
            // console.error('Error loading BOM header:', error);
            // If we can't fetch the full BOM header, create a minimal one from flattened fields
            if (bomHeaderName) {
              const minimalBomHeader: BomHeader = {
                id: bomHeaderId,
                bomNumber: bomHeaderNumber || '',
                bomName: bomHeaderName,
                version: bomHeaderVersion || '1.0',
                status: '',
                effectiveDate: '',
                expiryDate: '',
                description: '',
                totalCost: 0,
                laborCost: 0,
                overheadCost: 0,
                materialCost: 0,
                productionQuantity: 0,
                unitCost: 0,
                approvedBy: '',
                approvedDate: '',
                isActive: true,
                branchId: wo.branchId || 0,
                createdBy: 0,
                updatedBy: 0,
                createdAt: '',
                updatedAt: '',
                endgood: wo.endgood || {} as Endgood
              };
              setCurrentBomHeader(minimalBomHeader);
            }
          }
        }
        
        // Load material issues and work order operations for this work order
        await loadMaterialIssues(wo.id);
        await loadWorkOrderOperations(wo.id);
        
        // Load variant breakdowns if endgood is available
        if (wo.endgood) {
          await loadVariantBreakdowns(wo.endgood.id);
        }
      } else {
        toast.error('Failed to load work order details');
        navigate('/work-orders');
      }
    } catch (error) {
      // console.error('Error loading work order:', error);
      toast.error('Failed to load work order details');
      navigate('/work-orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, selectedBranch]);

  // Load work order data for edit/view modes
  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      loadWorkOrderData();
    }
  }, [isEditMode, isViewMode, id, loadWorkOrderData]);

  // Load sales orders for all modes - filtered by selected branch
  useEffect(() => {
    const loadSalesOrders = async () => {
      if (!selectedBranch) {
        setSalesOrders([]);
        return;
      }
      
      try {
        const response = await salesOrderApiService.getSalesOrders(1, 1000, selectedBranch);
        if (response.success) {
          setSalesOrders(response.data || []);
        }
      } catch (error) {
        // console.error('Error loading sales orders:', error);
        toast.error('Failed to load sales orders');
      }
    };
    
    loadSalesOrders();
  }, [selectedBranch]);
  
  // BOM data
  const [bomHeaders, setBomHeaders] = useState<BomHeader[]>([]);
  const [bomDetails, setBomDetails] = useState<BomDetail[]>([]);
  const [currentBomHeader, setCurrentBomHeader] = useState<BomHeader | null>(null);
  // Function to load BOM details for a specific BOM header
  const loadBomDetails = useCallback(async (bomHeaderId: number) => {
    try {
      const details = await bomDetailApiService.getBomDetailsByHeader(bomHeaderId);
      setBomDetails(details);
    } catch (error) {
      // console.error('Error loading BOM details:', error);
      setBomDetails([]);
    }
  }, []);
  
  // Raw materials from GRN details (legacy - kept for backward compatibility)
  const [availableRawMaterials, setAvailableRawMaterials] = useState<RawMaterial[]>([]);
  const [loadingRawMaterials, setLoadingRawMaterials] = useState(false);
  
  // Materials per BOM detail (new structure)
  const [materialsByBomDetail, setMaterialsByBomDetail] = useState<Map<number, RawMaterial[]>>(new Map());
  const [loadingMaterialsByBomDetail, setLoadingMaterialsByBomDetail] = useState<Map<number, boolean>>(new Map());
  const [processSeq1BomDetails, setProcessSeq1BomDetails] = useState<BomDetail[]>([]);
  const [processSeq1ProcessId, setProcessSeq1ProcessId] = useState<number | null>(null);
  
  // Selected BOM details per process (step 1: user selects which priority/BOM detail to use)
  const [selectedBomDetailsByProcess, setSelectedBomDetailsByProcess] = useState<Map<number, number>>(new Map());
  
  // Endgood variants
  const [availableVariants, setAvailableVariants] = useState<EndgoodVariant[]>([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [variantEntries, setVariantEntries] = useState<Array<{
    variantId: number;
    variant: EndgoodVariant;
    issueQty: string;
    fgQty: number;
  }>>([]);
  const [loadingRemainingQuantities, setLoadingRemainingQuantities] = useState(false);

  // Calculate total issue quantity from variant entries
  const totalVariantIssueQty = variantEntries.reduce((total, entry) => {
    const qty = parseFloat(entry.issueQty) || 0;
    return total + qty;
  }, 0);
  const [saving, setSaving] = useState(false);
  const [materialIssues, setMaterialIssues] = useState<any[]>([]);
  const [loadingMaterialIssues, setLoadingMaterialIssues] = useState(false);
  const [editingIssueId, setEditingIssueId] = useState<number | null>(null);
  const [editingIssueDate, setEditingIssueDate] = useState<string>('');
  const [editingIssueQty, setEditingIssueQty] = useState<string>('');
  const [updatingIssue, setUpdatingIssue] = useState(false);
  
  // Variant breakdown state
  const [variantBreakdowns, setVariantBreakdowns] = useState<any[]>([]);
  const [loadingVariantBreakdowns, setLoadingVariantBreakdowns] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState<number | null>(null);
  const [editingVariantMaterialIssued, setEditingVariantMaterialIssued] = useState<string>('');
  const [updatingVariant, setUpdatingVariant] = useState(false);
  
  

  // Helper function to check if category is "Internal Manufacturing Part" or "Internal Manufactured Parts"
  // Defined outside component to prevent recreation on every render
  const isInternalManufacturingParts = (categoryName: string | undefined): boolean => {
    if (!categoryName) return false;
    const lowerName = categoryName.toLowerCase().trim();
    return lowerName === 'internal manufacturing parts' || 
           lowerName === 'internal manufactured parts' ||
           lowerName === 'internal manufacturing part' ||
           lowerName === 'internal manufactured part';
  };

  const getBomLineCategoryName = (bd: BomDetail): string | undefined =>
    bd.rawCategory?.categoryName || bd.rawItem?.itemCategory?.categoryName;

  /** BOP lines: entered value = finished parts; issue to API = parts × BOM qty. Raw material: entered value = issue qty per line (no multiply). */
  const isBopBomLine = (bd: BomDetail): boolean => {
    const n = (getBomLineCategoryName(bd) || '').trim().toLowerCase();
    return n === 'bop';
  };

  const issueAmountForApi = (bd: BomDetail, issueQtyNum: number): number => {
    if (bd.quantity <= 0) return issueQtyNum;
    return isBopBomLine(bd) ? issueQtyNum * bd.quantity : issueQtyNum;
  };

  const partsFromIssueInputForLine = (bd: BomDetail, issueQtyNum: number): number => {
    if (bd.quantity <= 0) return 0;
    return isBopBomLine(bd) ? Math.floor(issueQtyNum) : Math.floor(issueQtyNum / bd.quantity);
  };

  // Function to load materials for a single BOM detail
  const loadMaterialsForBomDetail = useCallback(async (bomDetail: BomDetail, processSeq1ProcessId: number | null) => {
    const bomDetailId = bomDetail.id;
    
    // Set loading state for this BOM detail
    setLoadingMaterialsByBomDetail(prev => {
      const newMap = new Map(prev);
      newMap.set(bomDetailId, true);
      return newMap;
    });

    try {
      const categoryName = bomDetail.rawCategory?.categoryName || bomDetail.rawItem?.itemCategory?.categoryName;
      const isInternal = isInternalManufacturingParts(categoryName);
      const branchId = selectedBranch || user?.defaultBranchId;
      
      let materials: RawMaterial[] = [];

      if (isInternal && bomDetail.endgood && processSeq1ProcessId) {
        // Use WIP filter API for internal manufactured parts
        const filterRequest: WIPFilterRequest = {
          branchId: branchId,
          endgoodId: bomDetail.endgood.id,
          processId: processSeq1ProcessId
        };

        const wipResponse = await wipApiService.filterWIPTasks(filterRequest);
        
        if (wipResponse.success && wipResponse.data) {
          const wipTasks = wipResponse.data;
          
          materials = wipTasks.map((wip, wipIndex) => ({
            id: (bomDetail.id * 100000) + (wipIndex * 1000) + wipIndex,
            grnId: 0,
            grnNumber: 'N/A',
            heatNo: wip.workOrderNumber,
            itemName: bomDetail.endgood?.endgoodName || 'Unknown',
            itemSku: bomDetail.endgood?.sku || 'N/A',
            receivedQuantity: wip.wipQuantity,
            remainingQuantity: wip.wipQuantity,// - (wip.issueQuantity ?? 0),
            unit: bomDetail.unit || 'pcs',
            receivedDate: '',
            supplierName: 'Internal',
            rawItemId: bomDetail.endgood?.id || 0, // Use endgood ID as rawItemId for internal parts
            isWIP: true,
            lotNo: wip.workOrderNumber,
            processId: processSeq1ProcessId
          }));
        }
      } else if (bomDetail.rawItem) {
        // Use GRN consumption API for regular raw items
        const grnDetailList = await grnApiService.searchGRNDetailsByRawItems([bomDetail.rawItem.id], branchId) as any[];
        
        // Filter GRNs with remaining quantity > minimum threshold
        const workOrderConfig = getWorkOrderConfig();
        const minRemainingQty = workOrderConfig.minRemainingQuantity;
        const filteredGrnDetails = grnDetailList.filter((detail: any) => {
          const remainingQty = detail.remainingQuantity || 0;
          //const receivedQty = detail.receivedQuantity || 0;
          return remainingQty > minRemainingQty && remainingQty  > 0;
        });
        
        materials = filteredGrnDetails.map((detail, index) => {
          const uniqueId = bomDetail.rawItem!.id * 10000 + index;
          
          return {
            id: uniqueId,
            grnId: 0,
            grnNumber: detail.grnNumber,
            heatNo: detail.heatNo || 'N/A',
            itemName: detail.itemName,
            itemSku: detail.rawItemSku,
            receivedQuantity: detail.receivedQuantity || detail.originalQuantity,
            remainingQuantity: detail.remainingQuantity,
            unit: bomDetail.unit || bomDetail.rawItem?.itemUnit || '',
            receivedDate: detail.grnDate,
            supplierName: detail.supplierName || 'N/A',
            rawItemId: detail.rawItemId,
            nop: detail.nop,
            remainingNop: (detail as any).remaining_nop || (detail as any).remainingNop,
            isWIP: false
          };
        });
      }

      // Update materials for this BOM detail
      setMaterialsByBomDetail(prev => {
        const newMap = new Map(prev);
        newMap.set(bomDetailId, materials);
        
        // Update legacy availableRawMaterials (flatten all materials)
        const allMaterials: RawMaterial[] = [];
        newMap.forEach(mats => {
          allMaterials.push(...mats);
        });
        setAvailableRawMaterials(allMaterials);
        
        return newMap;
      });

      // Auto-select top (first) record for this BOM detail in create mode
      // ONLY for Priority 1. For other priorities, keep the table unselected
      // so the user explicitly picks a material row.
      if (isNewMode && materials.length > 0 && bomDetail.priority === 1) {
        setSelectedMaterials(prev => {
          const newMap = new Map(prev);
          const hasSelectionForThisBomDetail = materials.some(m => newMap.has(m.id));
          if (!hasSelectionForThisBomDetail) {
            newMap.set(materials[0].id, {
              materialId: materials[0].id,
              rawMaterial: materials[0],
              issueQty: '',
            });
          }
          return newMap;
        });
      }
    } catch (error) {
      // console.error(`Error loading materials for BOM detail ${bomDetailId}:`, error);
      setMaterialsByBomDetail(prev => {
        const newMap = new Map(prev);
        newMap.set(bomDetailId, []);
        return newMap;
      });
    } finally {
      setLoadingMaterialsByBomDetail(prev => {
        const newMap = new Map(prev);
        newMap.set(bomDetailId, false);
        return newMap;
      });
    }
  }, [selectedBranch, user?.defaultBranchId, isNewMode]);

  // Function to load GRN details for raw items in the selected BOM
  const loadGRNDetailsForBOM = useCallback(async (bomHeaderId: number) => {
    if (!bomHeaderId) {
      setAvailableRawMaterials([]);
      setProcessSeq1BomDetails([]);
      setMaterialsByBomDetail(new Map());
      return;
    }

    try {
      setLoadingRawMaterials(true);
      setLoadingRemainingQuantities(true);
      
      // Get BOM header to access process flow
      const bomHeader = await bomHeaderApiService.getBomHeaderById(bomHeaderId.toString());
      
      // Get BOM details to know which raw items are needed
      const bomDetails = await bomDetailApiService.getBomDetailsByHeader(bomHeaderId);
      
      // Get process flow to find process sequence 1
      let processSeq1ProcessId: number | null = null;
      if (bomHeader.processFlow?.id) {
        try {
          // Use PROCESS_STEPS.BY_PROCESS_FLOW endpoint to get all process steps
          const processSteps = await processFlowApiService.getProcessStepsByProcessFlow(bomHeader.processFlow.id);
          
          // Find process step with sequenceOrder === 1
          const processStep1 = processSteps.find(step => step.sequenceOrder === 1);
          if (processStep1 && processStep1.process) {
            processSeq1ProcessId = processStep1.process.id;
            setProcessSeq1ProcessId(processStep1.process.id); // Store in state for reuse
            // console.log('Found process sequence 1 processId:', processSeq1ProcessId);
          } else {
            // console.warn('No process step found with sequenceOrder === 1');
          }
        } catch (error) {
          // console.error('Error loading process steps for process flow:', error);
        }
      }
      
      // Filter BOM details to only show process sequence 1 (show all priorities)
      let seq1Details = bomDetails;
      if (processSeq1ProcessId) {
        seq1Details = bomDetails.filter(detail => detail.process.id === processSeq1ProcessId);
        // console.log(`Filtered to ${seq1Details.length} BOM details for process sequence 1`);
      }
      
      // Store process seq 1 BOM details (all priorities)
      setProcessSeq1BomDetails(seq1Details);
      
      // Group by process to determine if we need 2-step selection or separate tables
      const processGroups = new Map<number, BomDetail[]>();
      seq1Details.forEach(bomDetail => {
        const processId = bomDetail.process.id;
        if (!processGroups.has(processId)) {
          processGroups.set(processId, []);
        }
        processGroups.get(processId)!.push(bomDetail);
      });
      
      // For each process, determine selection strategy and auto-select if needed
      processGroups.forEach((bomDetails, processId) => {
        // Check if there are multiple priorities
        const uniquePriorities = new Set(bomDetails.map(bd => bd.priority ?? 'null'));
        const hasMultiplePriorities = uniquePriorities.size > 1;
        
        if (hasMultiplePriorities) {
          // 2-step case: Auto-select priority 1 and load materials for it
          const priority1BomDetail = bomDetails.find(bd => bd.priority === 1) || bomDetails[0];
          setSelectedBomDetailsByProcess(prev => {
            const newMap = new Map(prev);
            newMap.set(processId, priority1BomDetail.id);
            return newMap;
          });
          // Load materials only for the selected BOM detail (priority 1)
          loadMaterialsForBomDetail(priority1BomDetail, processSeq1ProcessId);
        } else {
          // Same priority case: Load materials for all BOM details (they'll be in separate tables)
          bomDetails.forEach(bomDetail => {
            loadMaterialsForBomDetail(bomDetail, processSeq1ProcessId);
          });
        }
      });
      
      // Keep legacy availableRawMaterials for backward compatibility (flatten all materials)
      const allMaterials: RawMaterial[] = [];
      materialsByBomDetail.forEach(materials => {
        allMaterials.push(...materials);
      });
      setAvailableRawMaterials(allMaterials);
    } catch (error) {
      // console.error('Error loading GRN details for BOM:', error);
      // Fallback to empty list
      setAvailableRawMaterials([]);
    } finally {
      setLoadingRawMaterials(false);
      setLoadingRemainingQuantities(false);
    }
  }, [selectedBranch, user?.defaultBranchId]);

  // Work Order Operations state
  const [workOrderOperations, setWorkOrderOperations] = useState<WorkOrderOperation[]>([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [editingOperationId, setEditingOperationId] = useState<number | null>(null);
  const [editingProductionQty, setEditingProductionQty] = useState<string>('');
  const [editingRejectQty, setEditingRejectQty] = useState<string>('');
  const [updatingOperation, setUpdatingOperation] = useState(false);

  // Update current sales order when selection changes
  useEffect(() => {
    if (selectedSalesOrder) {
      const order = salesOrders.find(o => o.id.toString() === selectedSalesOrder);
      if (order) {
        // Fetch sales order details to get actual quantities
        const loadSalesOrderDetails = async () => {
          try {
            setLoadingSalesOrderDetails(true);
            const detailsResponse = await salesOrderApiService.getSalesOrderDetails(order.id.toString());
            // console.log('📋 Sales Order Details Response:', detailsResponse);
            if (detailsResponse.success && detailsResponse.data) {
              // console.log('📦 Sales Order Details Data:', detailsResponse.data);
              // Build parts list from selected sales order's endgoods
              const uniqueParts = new Map<number, PartInfo>();
              detailsResponse.data.forEach((detail, index) => {
                //console.log(`🔍 Processing detail ${index}:`, detail);
                const eg = (detail as any).endgood;
                //console.log(`  Endgood extracted:`, eg);
                if (eg && eg.id && !uniqueParts.has(eg.id)) {
                  const partInfo: PartInfo = {
                    id: eg.id,
                    endgoodName: eg.endgoodName || eg.name || `Endgood #${eg.id}`,
                    partNumber: eg.partNumber || eg.partNo || '',
                    sku: eg.sku || '',
                    rmRequiredPerUnit: 1.0,
                  };
                  //console.log(`  ✅ Adding part:`, partInfo);
                  uniqueParts.set(eg.id, partInfo);
                } else {
                  // console.log(`  ⚠️ Skipping detail - eg:`, eg, `has id:`, eg?.id, `already in map:`, eg?.id ? uniqueParts.has(eg.id) : 'N/A');
                }
              });
              const partsArray = Array.from(uniqueParts.values());
              // console.log(`✅ Setting ${partsArray.length} parts:`, partsArray);
              setParts(partsArray);
              // Reset dependent selections when SO changes
              setSelectedPart(null);
              setCurrentBomHeader(null);
              setBomDetails([]);
              setAvailableRawMaterials([]);
              setSelectedMaterials(new Map());
              setProcessSeq1BomDetails([]);
              setMaterialsByBomDetail(new Map());
              // Calculate total quantity from all line items
              const totalQuantity = detailsResponse.data.reduce((sum, detail) => sum + detail.quantity, 0);
              
              setCurrentSalesOrder({
                id: order.id.toString(),
                orderNumber: order.soNumber,
                customerName: order.customerName || 'Unknown Customer',
                startDate: order.effectiveStartDate || '',
                endDate: order.effectiveEndDate || '',
                quantity: totalQuantity,
                availableQty: totalQuantity, // For now, assuming all quantity is available
                wipQty: 0,
                pendingQty: 0
              });
            } else {
              // Fallback if details can't be loaded
              setCurrentSalesOrder({
                id: order.id.toString(),
                orderNumber: order.soNumber,
                customerName: order.customerName || 'Unknown Customer',
                startDate: order.effectiveStartDate || '',
                endDate: order.effectiveEndDate || '',
                quantity: 0,
                availableQty: 0,
                wipQty: 0,
                pendingQty: 0
              });
              setParts([]);
            }
          } catch (error) {
            // console.error('Error loading sales order details:', error);
            // Fallback if details can't be loaded
            setCurrentSalesOrder({
              id: order.id.toString(),
              orderNumber: order.soNumber,
              customerName: order.customerName || 'Unknown Customer',
              startDate: order.effectiveStartDate || '',
              endDate: order.effectiveEndDate || '',
              quantity: 0,
              availableQty: 0,
              wipQty: 0,
              pendingQty: 0
            });
            setParts([]);
          } finally {
            setLoadingSalesOrderDetails(false);
          }
        };
        
        loadSalesOrderDetails();
      } else {
        setCurrentSalesOrder(null);
        setParts([]);
        setSelectedPart(null);
        setCurrentBomHeader(null);
        setBomDetails([]);
        setAvailableRawMaterials([]);
      }
    } else {
      setCurrentSalesOrder(null);
      setParts([]);
      setSelectedPart(null);
      setCurrentBomHeader(null);
      setBomDetails([]);
      setAvailableRawMaterials([]);
    }
  }, [selectedSalesOrder, salesOrders]);

  // Update current part when selection changes - fetch BOMs for that endgood
  useEffect(() => {
    const onSelectEndgood = async () => {
      if (!selectedPart) {
        setCurrentPart(null);
        setCurrentBomHeader(null);
        setBomDetails([]);
        setAvailableVariants([]);
        setVariantEntries([]);
        setAvailableRawMaterials([]);
        setSelectedMaterials(new Map());
        setProcessSeq1BomDetails([]);
        setMaterialsByBomDetail(new Map());
        return;
      }

      const part = parts.find(p => p.id === selectedPart);
      setCurrentPart(part || null);

      // Load endgood variants for the selected part
      loadVariantsForEndgood(selectedPart);

      try {
        const headers = await bomHeaderApiService.getBomHeadersByEndgood(selectedPart);
        setBomHeaders(headers);
        if (headers.length === 1) {
          // Auto-select the only BOM - GRN loading will be triggered by the BOM selection effect
          const bomHeader = headers[0];
          setCurrentBomHeader(bomHeader);
          await loadBomDetails(bomHeader.id);
        } else {
          setCurrentBomHeader(null);
          setBomDetails([]);
          setAvailableRawMaterials([]);
        }
      } catch (error) {
        // console.error('Failed to load BOM headers for endgood:', error);
        setBomHeaders([]);
        setCurrentBomHeader(null);
        setBomDetails([]);
        setAvailableRawMaterials([]);
      }
    };

    onSelectEndgood();
  }, [selectedPart, parts, loadBomDetails]);

  // Load GRN details when BOM is selected
  useEffect(() => {
    if (currentBomHeader?.id) {
      loadGRNDetailsForBOM(currentBomHeader.id);
    } else {
      setAvailableRawMaterials([]);
    }
  }, [currentBomHeader?.id, loadGRNDetailsForBOM]);

  // Filtered raw materials - show only items matching selected raw material's itemName
  const filteredRawMaterials = useMemo(() => {
    if (!selectedRawMaterial) {
      return availableRawMaterials;
    }
    const selected = availableRawMaterials.find(rm => rm.id === selectedRawMaterial);
    if (!selected) {
      return availableRawMaterials;
    }
    // Filter to show only items with the same itemName as the selected one
    return availableRawMaterials.filter(rm => rm.itemName === selected.itemName);
  }, [availableRawMaterials, selectedRawMaterial]);

  // Group materials by raw item for process seq 1
  const groupedMaterials = useMemo(() => {
    const groups = new Map<number, RawMaterial[]>();
    availableRawMaterials.forEach(rm => {
      const key = rm.rawItemId;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(rm);
    });
    return groups;
  }, [availableRawMaterials]);

  // Helper function to handle BOM detail selection (step 1)
  const handleBomDetailSelect = useCallback(async (processId: number, bomDetailId: number) => {
    // When user switches the selected BOM detail (priority) for a process,
    // drop any previously selected materials under that process.
    // Otherwise validation can still run against the old material rows.
    setSelectedMaterials(prev => {
      const newMap = new Map(prev);
      const processBomDetails = processSeq1BomDetails.filter(bd => bd.process.id === processId);
      for (const bd of processBomDetails) {
        const mats = materialsByBomDetail.get(bd.id) || [];
        for (const m of mats) {
          newMap.delete(m.id);
        }
      }
      return newMap;
    });

    setSelectedBomDetailsByProcess(prev => {
      const newMap = new Map(prev);
      newMap.set(processId, bomDetailId);
      return newMap;
    });
    
    // Load materials for the selected BOM detail (always load, even if previously loaded)
    const bomDetail = processSeq1BomDetails.find(bd => bd.id === bomDetailId);
    if (bomDetail) {
      // Use stored processSeq1ProcessId if available, otherwise try to get it
      if (processSeq1ProcessId) {
        await loadMaterialsForBomDetail(bomDetail, processSeq1ProcessId);
      } else {
        // Fallback: use the process ID from the BOM detail
        await loadMaterialsForBomDetail(bomDetail, bomDetail.process.id);
      }
    }
  }, [processSeq1BomDetails, processSeq1ProcessId, loadMaterialsForBomDetail, materialsByBomDetail]);
  
  // Get selected BOM detail for a process
  const getSelectedBomDetailForProcess = (processId: number): BomDetail | null => {
    const selectedBomDetailId = selectedBomDetailsByProcess.get(processId);
    if (!selectedBomDetailId) return null;
    return processSeq1BomDetails.find(bd => bd.id === selectedBomDetailId) || null;
  };

  // Material selection: BOP lines allow multiple heats (checkbox); raw material is single-select (radio).
  const handleMaterialSelect = (material: RawMaterial, bomDetailId: number) => {
    const bomDetail = processSeq1BomDetails.find(bd => bd.id === bomDetailId);
    if (!bomDetail) return;

    const isBop = isBopBomLine(bomDetail);

    setSelectedMaterials(prev => {
      const newMap = new Map(prev);

      if (isBop && newMap.has(material.id)) {
        newMap.delete(material.id);
        return newMap;
      }

      if (!isBop) {
        for (const [key] of Array.from(newMap.entries())) {
          const materialBomDetail = processSeq1BomDetails.find(bd => {
            const materials = materialsByBomDetail.get(bd.id) || [];
            return materials.some(m => m.id === key);
          });

          if (materialBomDetail && materialBomDetail.id === bomDetailId) {
            newMap.delete(key);
          }
        }
      }

      newMap.set(material.id, {
        materialId: material.id,
        rawMaterial: material,
        issueQty: ''
      });

      return newMap;
    });
  };

  const handleMaterialQtyChange = (materialId: number, qty: string) => {
    setSelectedMaterials(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(materialId);
      if (existing) {
        newMap.set(materialId, {
          ...existing,
          issueQty: qty
        });
      }
      return newMap;
    });
  };

  const getSelectedMaterialsForBomDetail = (bomDetailId: number): RawMaterial[] => {
    const materials = materialsByBomDetail.get(bomDetailId) || [];
    return materials.filter(m => selectedMaterials.has(m.id));
  };

  const hasSelectedMaterialForBomDetail = (bomDetailId: number): boolean =>
    getSelectedMaterialsForBomDetail(bomDetailId).length > 0;

  const getSelectedMaterialForBomDetail = (bomDetailId: number): RawMaterial | null =>
    getSelectedMaterialsForBomDetail(bomDetailId)[0] ?? null;

  const allocateBopIssueAcrossHeats = (
    bomDetail: BomDetail,
    issueQtyNum: number,
    selectedMats: RawMaterial[],
  ): Array<{ material: RawMaterial; calculatedQty: number }> => {
    let remainingNeed = issueAmountForApi(bomDetail, issueQtyNum);
    const allocations: Array<{ material: RawMaterial; calculatedQty: number }> = [];

    for (const mat of selectedMats) {
      if (remainingNeed <= 0) break;
      const qty = Math.min(remainingNeed, mat.remainingQuantity);
      if (qty > 0) {
        allocations.push({ material: mat, calculatedQty: qty });
        remainingNeed -= qty;
      }
    }

    return allocations;
  };

  const isMaterialSelected = (materialId: number) => {
    return selectedMaterials.has(materialId);
  };

  const getMaterialIssueQty = (materialId: number) => {
    return selectedMaterials.get(materialId)?.issueQty || '';
  };

  // Update current raw material when selection changes
  useEffect(() => {
    if (selectedRawMaterial) {
      const rm = availableRawMaterials.find(rm => rm.id === selectedRawMaterial);
      setCurrentRawMaterial(rm || null);
    } else {
      setCurrentRawMaterial(null);
    }
  }, [selectedRawMaterial, availableRawMaterials]);

  // Load variants for a specific endgood
  const loadVariantsForEndgood = async (endgoodId: number) => {
    if (!endgoodId) {
      setAvailableVariants([]);
      setVariantEntries([]);
      return;
    }

    setLoadingVariants(true);
    try {
      const variants = await endgoodVariantApiService.getVariantsByEndgoodId(endgoodId);
      setAvailableVariants(variants);
      
      // Initialize variant entries for bulk input
      const entries = variants.map(variant => ({
        variantId: variant.id,
        variant: variant,
        issueQty: '',
        fgQty: 0
      }));
      setVariantEntries(entries);
    } catch (error) {
      // console.warn('Failed to load endgood variants:', error);
      setAvailableVariants([]);
      setVariantEntries([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  // Handle variant entry quantity changes
  const handleVariantIssueQtyChange = (variantId: number, qty: string) => {
    setVariantEntries(prev => prev.map(entry => {
      if (entry.variantId === variantId) {
        const numericQty = parseFloat(qty) || 0;
        // Calculate FG quantity based on BOM details
        const totalRawMaterialPerPart = bomDetails.reduce((total, detail) => {
          return total + detail.quantity;
        }, 0);
        const fgQty = totalRawMaterialPerPart > 0 ? Math.floor(numericQty / totalRawMaterialPerPart) : 0;
        
        return {
          ...entry,
          issueQty: qty,
          fgQty: fgQty
        };
      }
      return entry;
    }));
  };

  // Initialize state for available raw materials
  useEffect(() => {
    setAvailableRawMaterials([]);
  }, []);

  // Load BOM headers only for edit/view modes; new mode fetches by endgood
  useEffect(() => {
    if (!(isEditMode || isViewMode)) return;
    const loadAllBomHeaders = async () => {
      try {
        const headers = await bomHeaderApiService.getBomHeaders();
        setBomHeaders(headers);
      } catch (error) {
        // console.error('Error loading BOM headers:', error);
      }
    };
    loadAllBomHeaders();
  }, [isEditMode, isViewMode]);
  
  // Function to handle editing material issue
  const handleEditIssue = (issue: any) => {
    setEditingIssueId(issue.id);
    setEditingIssueDate(issue.issueDate.split('T')[0]); // Convert to date format
    setEditingIssueQty(issue.issueQuantity.toString());
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingIssueId(null);
    setEditingIssueDate('');
    setEditingIssueQty('');
  };

  // Function to handle editing variant material issued
  const handleEditVariantMaterialIssued = (variant: any) => {
    setEditingVariantId(variant.id);
    setEditingVariantMaterialIssued(variant.materialIssued.toString());
  };

  // Function to cancel variant editing
  const handleCancelVariantEdit = () => {
    setEditingVariantId(null);
    setEditingVariantMaterialIssued('');
  };

  // Function to save edited variant material issued
  const handleSaveVariantEdit = async (variant: any) => {
    if (!editingVariantMaterialIssued.trim()) {
      toast.error('Please enter a valid material issued quantity');
      return;
    }

    setUpdatingVariant(true);
    try {
      const response = await workOrderVariantBreakdownApiService.updateWorkOrderVariantBreakdown(
        variant.id,
        { materialIssued: parseFloat(editingVariantMaterialIssued) }
      );

      if (response.success) {
        // Update the local state
        setVariantBreakdowns(prev => 
          prev.map(v => 
            v.id === variant.id 
              ? { ...v, materialIssued: parseFloat(editingVariantMaterialIssued) }
              : v
          )
        );
        toast.success('Variant material issued updated successfully');
        handleCancelVariantEdit();
      } else {
        toast.error(response.message || 'Failed to update variant material issued');
      }
    } catch (error) {
      // console.error('Error updating variant material issued:', error);
      toast.error('Failed to update variant material issued');
    } finally {
      setUpdatingVariant(false);
    }
  };

  // Function to save edited material issue
  const handleSaveEdit = async (issue: any) => {
    if (!editingIssueDate || !editingIssueQty) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUpdatingIssue(true);
      const updateData = {
        ...issue,
        issueDate: editingIssueDate,
        issueQuantity: parseFloat(editingIssueQty),
        consumedQuantity: parseFloat(editingIssueQty), // Keep same as issue quantity
      };

      const response = await materialIssueApiService.updateMaterialIssue(issue.id.toString(), updateData);
      
      if (response.success) {
        toast.success('Material issue updated successfully!');
        // Refresh material issues
        if (workOrder) {
          await loadMaterialIssues(workOrder.id);
        }
        handleCancelEdit();
      } else {
        toast.error('Failed to update material issue');
      }
    } catch (error) {
      // console.error('Error updating material issue:', error);
      toast.error('Failed to update material issue');
    } finally {
      setUpdatingIssue(false);
    }
  };

  // Function to load work order operations
  const loadWorkOrderOperations = async (workOrderId: number) => {
    try {
      setLoadingOperations(true);
      const response = await workOrderOperationApiService.getWorkOrderOperationsByWorkOrder(workOrderId.toString());
      if (response.success && response.data) {
        setWorkOrderOperations(response.data);
      } else {
        setWorkOrderOperations([]);
      }
    } catch (error) {
      // console.error('Error loading work order operations:', error);
      setWorkOrderOperations([]);
    } finally {
      setLoadingOperations(false);
    }
  };

  // Function to handle editing work order operation
  const handleEditOperation = (operation: WorkOrderOperation) => {
    setEditingOperationId(operation.id);
    setEditingProductionQty(operation.quantityCompleted.toString());
    setEditingRejectQty(operation.quantityRejected.toString());
  };

  // Function to cancel editing operation
  const handleCancelEditOperation = () => {
    setEditingOperationId(null);
    setEditingProductionQty('');
    setEditingRejectQty('');
  };

  // Function to save edited work order operation
  const handleSaveEditOperation = async (operation: WorkOrderOperation) => {
    if (!editingProductionQty || !editingRejectQty) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUpdatingOperation(true);
      const updateData = {
        id: operation.id,
        operationNumber: operation.operationNumber,
        operationName: operation.operationName,
        operationDescription: operation.operationDescription,
        workCenter: operation.workCenter,
        machineCode: operation.machineCode,
        setupTimeHours: operation.setupTimeHours,
        runTimeHoursPerUnit: operation.runTimeHoursPerUnit,
        totalEstimatedHours: operation.totalEstimatedHours,
        actualSetupHours: operation.actualSetupHours,
        actualRunHours: operation.actualRunHours,
        totalActualHours: operation.totalActualHours,
        quantityCompleted: parseFloat(editingProductionQty),
        quantityRejected: parseFloat(editingRejectQty),
        rejectionReason: operation.rejectionReason,
        status: operation.status,
        startTime: operation.startTime,
        endTime: operation.endTime,
        assignedOperator: operation.assignedOperator,
        qualityCheckStatus: operation.qualityCheckStatus,
        qualityCheckNotes: operation.qualityCheckNotes,
        isActive: operation.isActive,
        workOrderId: operation.workOrder.id,
        processStepId: operation.processStep?.id,
      };

      const response = await workOrderOperationApiService.updateWorkOrderOperation(operation.id.toString(), updateData);
      
      if (response.success) {
        toast.success('Work order operation updated successfully!');
        // Refresh work order operations
        if (workOrder) {
          await loadWorkOrderOperations(workOrder.id);
        }
        handleCancelEditOperation();
      } else {
        toast.error('Failed to update work order operation');
      }
    } catch (error) {
      // console.error('Error updating work order operation:', error);
      toast.error('Failed to update work order operation');
    } finally {
      setUpdatingOperation(false);
    }
  };

  // Function to load material issues for a work order
  const loadMaterialIssues = async (workOrderId: number) => {
    try {
      setLoadingMaterialIssues(true);
      const response = await materialIssueApiService.getMaterialIssuesByWorkOrder(workOrderId.toString());
      if (response.success) {
        setMaterialIssues(response.data);
      } else {
        setMaterialIssues([]);
      }
    } catch (error) {
      // console.error('Error loading material issues:', error);
      setMaterialIssues([]);
    } finally {
      setLoadingMaterialIssues(false);
    }
  };

  // Function to load variant breakdowns for an endgood
  const loadVariantBreakdowns = async (endgoodId: number) => {
    try {
      setLoadingVariantBreakdowns(true);
      const response = await workOrderVariantBreakdownApiService.getWorkOrderVariantBreakdownsByEndgood(endgoodId);
      if (response.success) {
        setVariantBreakdowns(response.data || []);
      } else {
        setVariantBreakdowns([]);
      }
    } catch (error) {
      // console.error('Error loading variant breakdowns:', error);
      setVariantBreakdowns([]);
    } finally {
      setLoadingVariantBreakdowns(false);
    }
  };

  // Finished parts = min over selected lines (BOP: input is parts; RM: ⌊input ÷ BOM qty⌋).
  useEffect(() => {
    const q = issueQty ? parseFloat(issueQty) : NaN;
    if (!issueQty || isNaN(q) || q <= 0) {
      setCalculatedParts(0);
      return;
    }
    let minParts = Infinity;
    for (const bomDetail of processSeq1BomDetails) {
      if (!hasSelectedMaterialForBomDetail(bomDetail.id) || bomDetail.quantity <= 0) continue;
      minParts = Math.min(minParts, partsFromIssueInputForLine(bomDetail, q));
    }
    setCalculatedParts(minParts === Infinity ? 0 : minParts);
  }, [issueQty, processSeq1BomDetails, selectedMaterials, materialsByBomDetail]);

  /** Max value for the issue field: per-line cap combined (BOP = max parts ⌊rem÷BOM⌋, RM = max issue = remaining). */
  const getMaxAllowedIssueQuantity = (): number => {
    if (processSeq1BomDetails.length === 0 || selectedMaterials.size === 0) {
      return Infinity;
    }

    let maxInput = Infinity;

    for (const bomDetail of processSeq1BomDetails) {
      const selected = getSelectedMaterialsForBomDetail(bomDetail.id);
      if (selected.length === 0 || bomDetail.quantity <= 0) continue;

      const cap = isBopBomLine(bomDetail)
        ? Math.floor(
            selected.reduce((sum, m) => sum + m.remainingQuantity, 0) / bomDetail.quantity,
          )
        : selected[0].remainingQuantity;
      maxInput = Math.min(maxInput, cap);
    }

    return maxInput === Infinity ? 0 : maxInput;
  };

  const isIssueQuantityExceeded = () => {
    if (!issueQty || isNaN(parseFloat(issueQty)) || parseFloat(issueQty) <= 0) {
      return false;
    }

    const issueQtyNum = parseFloat(issueQty);

    for (const bomDetail of processSeq1BomDetails) {
      const selected = getSelectedMaterialsForBomDetail(bomDetail.id);
      if (selected.length === 0 || bomDetail.quantity <= 0) continue;

      const need = issueAmountForApi(bomDetail, issueQtyNum);
      const available = isBopBomLine(bomDetail)
        ? selected.reduce((sum, m) => sum + m.remainingQuantity, 0)
        : selected[0].remainingQuantity;
      if (need > available) {
        return true;
      }
    }

    return false;
  };

  const handleIssueQtyChange = (value: string) => {
    if (value === '') {
      setIssueQty('');
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    const numericValue = parseFloat(value);

    if (!isNaN(numericValue)) {
      const maxInput = getMaxAllowedIssueQuantity();
      if (maxInput !== Infinity && maxInput > 0 && numericValue > maxInput) {
        toast.error(`Issue quantity cannot exceed ${maxInput} for the selected batches and BOM (BOP vs raw material rules)`);
        setIssueQty(maxInput.toString());
        return;
      }
    }

    setIssueQty(value);
  };

  const handleSave = async () => {
    if (!selectedSalesOrder || !selectedPart) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Check if any materials are selected
    if (selectedMaterials.size === 0 || processSeq1BomDetails.length === 0) {
      toast.error("Please select at least one material for process sequence 1");
      return;
    }

    if (!issueQty || isNaN(parseFloat(issueQty)) || parseFloat(issueQty) <= 0) {
      toast.error("Please enter a valid issue quantity");
      return;
    }

    const issueQtyNum = parseFloat(issueQty);

    let minParts = Infinity;
    for (const bomDetail of processSeq1BomDetails) {
      if (!hasSelectedMaterialForBomDetail(bomDetail.id) || bomDetail.quantity <= 0) continue;
      minParts = Math.min(minParts, partsFromIssueInputForLine(bomDetail, issueQtyNum));
    }
    const finalPartsPreview = minParts === Infinity ? 0 : minParts;
    if (finalPartsPreview < 1) {
      toast.error("Issue quantity is too small to produce at least one part for the selected BOM lines");
      return;
    }

    const invalidMaterials: string[] = [];
    const materialsToIssue: Array<{ material: RawMaterial; bomDetail: BomDetail; calculatedQty: number }> = [];

    for (const bomDetail of processSeq1BomDetails) {
      const selected = getSelectedMaterialsForBomDetail(bomDetail.id);
      if (selected.length === 0) continue;

      const categoryName = getBomLineCategoryName(bomDetail);
      const isInternal = isInternalManufacturingParts(categoryName);
      const itemName = isInternal
        ? (bomDetail.endgood?.endgoodName || 'Unknown')
        : (bomDetail.rawItem?.itemName || 'Unknown');

      if (bomDetail.quantity <= 0) {
        invalidMaterials.push(`${itemName} (invalid BOM quantity)`);
        continue;
      }

      const totalNeed = issueAmountForApi(bomDetail, issueQtyNum);
      const available = isBopBomLine(bomDetail)
        ? selected.reduce((sum, m) => sum + m.remainingQuantity, 0)
        : selected[0].remainingQuantity;

      if (totalNeed > available) {
        invalidMaterials.push(
          `${itemName} (need: ${totalNeed.toFixed(6)}, available: ${available})`,
        );
        continue;
      }

      if (isBopBomLine(bomDetail)) {
        const allocations = allocateBopIssueAcrossHeats(bomDetail, issueQtyNum, selected);
        for (const alloc of allocations) {
          materialsToIssue.push({
            material: alloc.material,
            bomDetail,
            calculatedQty: alloc.calculatedQty,
          });
        }
      } else {
        materialsToIssue.push({
          material: selected[0],
          bomDetail,
          calculatedQty: totalNeed,
        });
      }
    }
    
    // Check if all required BOM details have selected materials
    const requiredBomDetails = processSeq1BomDetails.filter(bd => {
      // Check if any selected material belongs to this BOM detail
      for (const [materialId] of Array.from(selectedMaterials.entries())) {
        const materials = materialsByBomDetail.get(bd.id) || [];
        if (materials.some(m => m.id === materialId)) {
          return true;
        }
      }
      return false;
    });
    
    if (requiredBomDetails.length < processSeq1BomDetails.length) {
      const missingBomDetails = processSeq1BomDetails.filter(bd => {
        for (const [materialId] of Array.from(selectedMaterials.entries())) {
          const materials = materialsByBomDetail.get(bd.id) || [];
          if (materials.some(m => m.id === materialId)) {
            return false;
          }
        }
        return true;
      });
      
      const missingNames = missingBomDetails.map(bd => {
        const categoryName = bd.rawCategory?.categoryName || bd.rawItem?.itemCategory?.categoryName;
        const isInternal = isInternalManufacturingParts(categoryName);
        return isInternal 
          ? (bd.endgood?.endgoodName || 'Unknown')
          : (bd.rawItem?.itemName || 'Unknown');
      });
      
      // if (missingNames.length > 0) {
      //   toast.error(`Please select materials for: ${missingNames.join(', ')}`);
      //   return;
      // }
    }

    if (invalidMaterials.length > 0) {
      toast.error(`Calculated quantities exceed available for: ${invalidMaterials.join(', ')}`);
      return;
    }

    if (materialsToIssue.length === 0) {
      toast.error("Please select all required materials");
      return;
    }

    setSaving(true);
    try {
      const finalCalculatedParts = finalPartsPreview;

      let estimatedHours: number;
      if (currentBomHeader?.processFlow?.totalLaborHours && currentBomHeader.processFlow.totalLaborHours > 0) {
        estimatedHours = finalCalculatedParts * currentBomHeader.processFlow.totalLaborHours;
      } else {
        estimatedHours = finalCalculatedParts * 2;
      }

      const workOrderData = {
        customerName: currentSalesOrder?.customerName || 'Unknown Customer',
        quantity: finalCalculatedParts,
        priority: WorkOrderPriority.MEDIUM,
        status: WorkOrderStatus.IN_PROGRESS,
        startDate: issueDate,
        dueDate: currentSalesOrder?.endDate || issueDate,
        assignedTo: "Production Team", // TODO: Get from user context
        department: "Production",
        estimatedHours: estimatedHours,
        actualHours: 0,
        notes: `Work order created for ${currentPart?.endgoodName || 'Unknown Part'} based on sales order ${currentSalesOrder?.orderNumber || 'N/A'}`,
        branchId: selectedBranch || user?.defaultBranchId || 1,
        endgoodId: selectedPart,
        salesOrderId: parseInt(selectedSalesOrder),
        bomHeaderId: currentBomHeader?.id
      };

      const workOrderResponse = await workOrderApiService.createWorkOrder(workOrderData);
      
      if (!workOrderResponse.success || !workOrderResponse.data) {
        toast.error("Failed to create work order");
        return;
      }

      const createdWorkOrder = workOrderResponse.data;

      // Create material issues for all selected materials using calculated quantities (bulk create)
      const materialIssuesData: MaterialIssueCreateRequest[] = materialsToIssue.map((item) => {
        // Determine if this is an internal manufactured part
        const categoryName = item.bomDetail.rawCategory?.categoryName || item.bomDetail.rawItem?.itemCategory?.categoryName;
        const isInternal = isInternalManufacturingParts(categoryName);
        
        // Get rawCategoryId
        const rawCategoryId = item.bomDetail.rawCategoryId || item.bomDetail.rawCategory?.id || item.bomDetail.rawItem?.itemCategory?.id || 0;
        
        console.log('📦 Material Issue Calculation:', {
          materialName: item.material.itemName,
          enteredQty: issueQtyNum,
          isBop: isBopBomLine(item.bomDetail),
          requiredPerPart: item.bomDetail.quantity,
          finishedParts: finalCalculatedParts,
          issueQuantityToApi: item.calculatedQty,
          unit: item.material.unit
        });

        const materialIssueData: MaterialIssueCreateRequest = {
          issueDate: issueDate,
          issueQuantity: item.calculatedQty,
          consumedQuantity: item.calculatedQty,
          returnedQuantity: 0,
          wastedQuantity: 0,
          issueType: MaterialIssueType.PRODUCTION,
          status: MaterialIssueStatus.ISSUED,
          issueReason: `Material issued for production - ${currentPart?.endgoodName || 'Unknown Part'}`,
          issuedBy: "System", // TODO: Get from user context
          receivedBy: "Production", // TODO: Get from user context
          heatNumber: item.material.heatNo || '',
          notes: `Material issued for work order ${createdWorkOrder.workOrderNumber}. Issue qty: ${item.calculatedQty.toFixed(6)} ${item.material.unit || 'units'}${isBopBomLine(item.bomDetail) ? ` (BOP: ${issueQtyNum} parts × ${item.bomDetail.quantity} BOM)` : ` (raw material)`}; WO ${finalCalculatedParts} parts`,
          workOrderId: createdWorkOrder.id,
          branchId: selectedBranch ?? 0,
          rawCategoryId: rawCategoryId,
          // If internal manufactured parts, use endgoodId; otherwise use rawItemId
          ...(isInternal 
            ? { endgoodId: item.bomDetail.endgood?.id || item.bomDetail.endgoodId || 0 }
            : { rawItemId: item.bomDetail.rawItem?.id || item.material.rawItemId || 0 }
          )
        };

        // console.log('📤 Material Issue Data:', materialIssueData);
        return materialIssueData;
      });

      const materialIssueResponse = await materialIssueApiService.createBulkMaterialIssues(materialIssuesData);
      
      if (!materialIssueResponse.success || !materialIssueResponse.data || materialIssueResponse.data.length === 0) {
        toast.error("Failed to create material issues");
        return;
      }
      
      const successfulIssues = materialIssueResponse.data.length;
      
      if (materialIssueResponse.success) {
        // If variants are available, create work order variant breakdowns
        if (availableVariants.length > 0 && variantEntries.length > 0) {
          try {
            const variantBreakdowns: WorkOrderVariantBreakdownCreateRequest[] = variantEntries.map(entry => ({
              quantity: entry.fgQty, // FG (Finished Goods) quantity
              completedQuantity: 0, // Initially 0, will be updated during production
              rejectedQuantity: 0, // Initially 0, will be updated during production
              materialIssued: parseFloat(entry.issueQty) || 0, // Raw material issued quantity
              isActive: true,
              workOrderId: createdWorkOrder.id,
              endgoodId: selectedPart,
              endgoodVariantId: entry.variantId
            }));

            const variantBreakdownResponse = await workOrderVariantBreakdownApiService.createBulkWorkOrderVariantBreakdowns(variantBreakdowns);
            
            if (variantBreakdownResponse.success) {
              toast.success(`Work order ${createdWorkOrder.workOrderNumber} created successfully with ${successfulIssues} material issue(s) and variant breakdowns!`);
            } else {
              // console.warn('Work order created but variant breakdowns failed:', variantBreakdownResponse.error);
              toast.success(`Work order ${createdWorkOrder.workOrderNumber} created successfully with ${successfulIssues} material issue(s)!`);
            }
          } catch (variantError) {
            // console.error('Error creating variant breakdowns:', variantError);
            toast.success(`Work order ${createdWorkOrder.workOrderNumber} created successfully with ${successfulIssues} material issue(s)!`);
          }
        } else {
          toast.success(`Work order ${createdWorkOrder.workOrderNumber} created successfully with ${successfulIssues} material issue(s)!`);
        }
        
        // Navigate back to work order listing
        navigate('/work-orders');
      } else {
        toast.warning(`Work order created but only ${successfulIssues} of ${materialsToIssue.length} material issues were created successfully.`);
        navigate('/work-orders');
      }
    } catch (error) {
      // console.error('Error creating work order:', error);
      toast.error("Failed to create work order");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedSalesOrder("");
    setSelectedPart(null);
    setSelectedRawMaterial(null);
    setSelectedMaterials(new Map());
    setIssueQty("");
    setIssueDate("");
    toast.info("Form cancelled");
  };

  // Check if all prerequisites are met
  const canShowRawMaterials = selectedSalesOrder && selectedPart && currentBomHeader;

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      {isViewMode ? (
        <div className="flex items-center justify-center relative">
          <div className="absolute left-0">
            <Button
              variant="outline"
              onClick={() => navigate('/work-orders')}
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Listing
            </Button>
          </div>
          <div className="text-center space-y-2">
            <h1 className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
              Work Order
            </h1>
            <p className="text-gray-600">
              View work order details and production progress
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/work-orders')}
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Listing
            </Button>
            <div className="text-center space-y-2">
              <h1 className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                {isNewMode && 'Work Order'}
                {isEditMode && 'Edit Work Order'}
              </h1>
              <p className="text-gray-600">
                {isNewMode && 'Create a work order for production based on sales orders'}
                {isEditMode && 'Edit work order information and status'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sales Order Selection */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            Sales Order Information
          </CardTitle>
          <CardDescription>
            Select the sales order and part for raw material issuance
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {isViewMode && workOrder && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-200">
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Work Order Number</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-800">{workOrder.workOrderNumber}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Work Order Quantity</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-800">{workOrder.quantity} units</span>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-6">
            <div className="space-y-2 min-w-0">
              <Label htmlFor="salesOrder">Open Sales Order *</Label>
              {isViewMode || isEditMode ? (
                // Read-only display in view/edit mode using flattened salesOrderNumber and customerName
                <Input
                  value={workOrder?.salesOrderNumber ? (
                    `${workOrder.salesOrderNumber}${workOrder.customerName ? ` - ${workOrder.customerName}` : ''}`
                  ) : 'N/A'}
                  disabled
                  className="bg-gray-50 border-gray-200 text-gray-700 truncate"
                  title={
                    workOrder?.salesOrderNumber
                      ? `${workOrder.salesOrderNumber}${workOrder.customerName ? ` - ${workOrder.customerName}` : ''}`
                      : undefined
                  }
                />
              ) : (
                <Select value={selectedSalesOrder} onValueChange={setSelectedSalesOrder}>
                  <SelectTrigger
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 min-w-0 max-w-full overflow-hidden [&>span]:truncate [&>span]:min-w-0"
                    title={
                      selectedSalesOrder
                        ? (() => {
                            const order = salesOrders.find((o) => o.id.toString() === selectedSalesOrder);
                            return order
                              ? `${order.soNumber} - ${order.customerName || 'Unknown Customer'}`
                              : undefined;
                          })()
                        : undefined
                    }
                  >
                    <SelectValue placeholder="Select a sales order" />
                  </SelectTrigger>
                  <SelectContentWithSearch>
                    {salesOrders.map((order) => (
                      <SelectItem key={order.id} value={order.id.toString()}>
                        {order.soNumber} - {order.customerName || 'Unknown Customer'}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 min-w-0">
              <Label htmlFor="partName">Endgood Name *</Label>
              {isViewMode || isEditMode ? (
                // Read-only display in view/edit mode
                <Input
                  value={
                    workOrder?.endgoodName || 
                    (selectedPart ? (
                      parts.find(p => p.id === selectedPart)?.endgoodName || `Part ID: ${selectedPart}`
                    ) : 'No part selected')
                  }
                  disabled
                  className="bg-gray-50 border-gray-200 text-gray-700 truncate"
                />
              ) : (
                <Select 
                  value={selectedPart?.toString() || ""} 
                  onValueChange={(value) => {
                    // console.log('🔵 Part selected:', value);
                    setSelectedPart(value ? parseInt(value) : null);
                  }}
                  disabled={!selectedSalesOrder || loadingSalesOrderDetails}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 min-w-0 max-w-full overflow-hidden [&>span]:truncate [&>span]:min-w-0">
                    <SelectValue placeholder={
                      loadingSalesOrderDetails 
                        ? "Loading parts..." 
                        : selectedSalesOrder 
                          ? parts.length === 0 
                            ? "No parts available" 
                            : "Select a part" 
                          : "Select sales order first"
                    } />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search endgoods...">
                    {loadingSalesOrderDetails ? (
                      <SelectItem value="loading" disabled>
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                          Loading parts...
                        </div>
                      </SelectItem>
                    ) : parts.length === 0 ? (
                      <SelectItem value="no-parts" disabled>
                        No parts available
                      </SelectItem>
                    ) : (
                      parts.map((part) => {
                        //console.log('🟢 Rendering part in dropdown:', part);
                        return (
                          <SelectItem key={part.id} value={part.id.toString()}>
                            {part.endgoodName}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContentWithSearch>
                </Select>
              )}
            </div>

            <div className="space-y-2 min-w-0">
              <Label htmlFor="bomSelection">BOM *</Label>
              {isViewMode || isEditMode ? (
                // Read-only display in view/edit mode
                <Input
                  value={currentBomHeader ? (
                    `${currentBomHeader.bomName} v${currentBomHeader.version}`
                  ) : 'No BOM selected'}
                  disabled
                  className="bg-gray-50 border-gray-200 text-gray-700"
                />
              ) : (
                <Select 
                  value={currentBomHeader?.id?.toString() || ""} 
                  onValueChange={(value) => {
                    const bomHeader = bomHeaders.find(bom => bom.id.toString() === value);
                    if (bomHeader) {
                      setCurrentBomHeader(bomHeader);
                      loadBomDetails(bomHeader.id);
                      // GRN loading will be triggered by the useEffect watching currentBomHeader
                    }
                  }}
                  disabled={!selectedPart || bomHeaders.filter(bom => bom.endgood?.id === selectedPart).length === 0}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 min-w-0 max-w-full overflow-hidden [&>span]:truncate [&>span]:min-w-0">
                    <SelectValue placeholder={selectedPart ? "Select BOM" : "Select part first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {bomHeaders
                      .filter(bom => bom.endgood?.id === selectedPart)
                      .map((bom) => (
                        <SelectItem key={bom.id} value={bom.id.toString()}>
                          {bom.bomName} v{bom.version}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            </div>
          </div>

          {/* Order Details (Read-only) */}
          {currentSalesOrder && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Start Date</Label>
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary-600" />
                  <span className="text-sm font-medium">{currentSalesOrder.startDate}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">End Date</Label>
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary-600" />
                  <span className="text-sm font-medium">{currentSalesOrder.endDate}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Sales Order Quantity</Label>
                {loadingSalesOrderDetails ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-500"></div>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : (
                  <span className="text-sm font-medium">{currentSalesOrder.quantity} units</span>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Available Qty</Label>
                {loadingSalesOrderDetails ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-500"></div>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : (
                  <span className="text-sm font-medium text-green-600">{currentSalesOrder.availableQty} units</span>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">WIP Qty</Label>
                <span className="text-sm font-medium text-blue-600">{currentSalesOrder.wipQty} units</span>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Pending Qty</Label>
                <span className="text-sm font-medium text-orange-600">{currentSalesOrder.pendingQty} units</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw Material Selection Table - Hidden in view and edit mode */}
      {!isViewMode && !isEditMode && (
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            Raw Material Selection
          </CardTitle>
          <CardDescription>
            {isNewMode 
              ? "Available raw materials from GRN details based on the selected BOM"
              : "Raw materials used for this work order (read-only)"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {canShowRawMaterials ? (
            loadingRawMaterials ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4"></div>
                <p className="text-gray-600">Loading available raw materials...</p>
              </div>
            ) : processSeq1BomDetails.length > 0 ? (
              <div className="space-y-6">
                {(() => {
                  // Group BOM details by process
                  const processGroups = new Map<number, BomDetail[]>();
                  processSeq1BomDetails.forEach(bomDetail => {
                    const processId = bomDetail.process.id;
                    if (!processGroups.has(processId)) {
                      processGroups.set(processId, []);
                    }
                    processGroups.get(processId)!.push(bomDetail);
                  });

                  return Array.from(processGroups.entries()).map(([processId, bomDetails]) => {
                    const process = bomDetails[0].process;
                    const processName = process.processName;
                    const selectedBomDetail = getSelectedBomDetailForProcess(processId);
                    
                    // Check if there are multiple priorities for this process
                    const uniquePriorities = new Set(bomDetails.map(bd => bd.priority ?? 'null'));
                    const hasMultiplePriorities = uniquePriorities.size > 1;
                    
                    // Check if there are multiple BOM details with the same priority
                    const priorityGroups = new Map<string | number, BomDetail[]>();
                    bomDetails.forEach(bd => {
                      const priority = bd.priority ?? 'null';
                      if (!priorityGroups.has(priority)) {
                        priorityGroups.set(priority, []);
                      }
                      priorityGroups.get(priority)!.push(bd);
                    });
                    const hasMultipleBomDetailsWithSamePriority = Array.from(priorityGroups.values()).some(group => group.length > 1);
                    
                    return (
                      <Card key={processId} className="border-blue-200 shadow-md">
                        <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100/50 border-b border-blue-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-blue-800">{processName}</h4>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-6 space-y-4">
                          {/* Case 1: Multiple priorities → 2-step selection */}
                          {hasMultiplePriorities ? (
                            <>
                              {/* Step 1: BOM Detail Selection */}
                              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <Label className="text-sm font-medium text-blue-800 mb-2 block">
                                  Select BOM Detail (Priority):
                                </Label>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {[...bomDetails].sort((a, b) => {
                                    const priorityA = a.priority ?? 999;
                                    const priorityB = b.priority ?? 999;
                                    return priorityA - priorityB;
                                  }).map((bomDetail) => {
                                    const categoryName = bomDetail.rawCategory?.categoryName || bomDetail.rawItem?.itemCategory?.categoryName;
                                    const isInternal = isInternalManufacturingParts(categoryName);
                                    const itemName = isInternal 
                                      ? (bomDetail.endgood?.endgoodName || 'Unknown')
                                      : (bomDetail.rawItem?.itemName || 'Unknown');
                                    const isSelected = selectedBomDetail?.id === bomDetail.id;
                                    
                                    return (
                                      <div
                                        key={bomDetail.id}
                                        onClick={() => handleBomDetailSelect(processId, bomDetail.id)}
                                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                          isSelected
                                            ? 'border-blue-500 bg-blue-100'
                                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="radio"
                                            name={`bom-detail-${processId}`}
                                            checked={isSelected}
                                            onChange={() => handleBomDetailSelect(processId, bomDetail.id)}
                                            className="h-4 w-4 text-blue-600"
                                          />
                                          <div className="flex-1">
                                            <div className="font-medium text-sm">{itemName}</div>
                                            <div className="text-xs text-gray-600">
                                              Priority: {bomDetail.priority ?? 'N/A'} | BOM Qty (for 1 part): {bomDetail.quantity} {bomDetail.unit}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              
                              {/* Step 2: Show materials table for selected BOM detail (auto-selected priority 1) */}
                              {selectedBomDetail ? (
                                (() => {
                                  const materials = materialsByBomDetail.get(selectedBomDetail.id) || [];
                                  const filteredMaterials = materials.filter(rm => {
                                    const remainingQty = rm.remainingQuantity ?? 0;
                                    //const receivedQty = rm.receivedQuantity ?? 0;
                                    return remainingQty > 0;
                                  });
                                  const isLoading = loadingMaterialsByBomDetail.get(selectedBomDetail.id) || false;
                                  const categoryName = selectedBomDetail.rawCategory?.categoryName || selectedBomDetail.rawItem?.itemCategory?.categoryName;
                                  const isInternal = isInternalManufacturingParts(categoryName);
                                  const itemName = isInternal 
                                    ? (selectedBomDetail.endgood?.endgoodName || 'Unknown')
                                    : (selectedBomDetail.rawItem?.itemName || 'Unknown');
                                  const isBopLine = isBopBomLine(selectedBomDetail);
                                  
                                  return (
                                    <>
                                      <div className="mb-2 text-sm text-gray-600">
                                        <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs mr-2">
                                          {itemName}
                                        </Badge>
                                        {selectedBomDetail.priority !== undefined && (
                                          <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs">
                                            Priority: {selectedBomDetail.priority} | BOM Qty (for 1 part): {selectedBomDetail.quantity} {selectedBomDetail.unit}
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      {isLoading ? (
                                        <div className="flex flex-col items-center justify-center py-8">
                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                                          <p className="text-gray-600 text-sm">Loading materials...</p>
                                        </div>
                                      ) : filteredMaterials.length > 0 ? (
                                        <div className="rounded-lg border border-primary-200 overflow-hidden">
                                          <Table>
                                            <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                                              <TableRow className="border-primary-200">
                                                {isNewMode && <TableHead className="w-12 text-center">Select</TableHead>}
                                                {!filteredMaterials[0]?.isWIP && <TableHead className="text-left">GRN Number</TableHead>}
                                                <TableHead className="text-center">
                                                  {filteredMaterials[0]?.isWIP ? 'Lot No. (WO)' : 'Heat No.'}
                                                </TableHead>
                                                <TableHead className="text-center">Received Qty</TableHead>
                                                <TableHead className="text-center">Remaining NOP</TableHead>
                                                <TableHead className="text-center">Remaining Qty</TableHead>
                                                <TableHead className="text-center">Unit</TableHead>
                                                {!filteredMaterials[0]?.isWIP && <TableHead className="text-center">Received Date</TableHead>}
                                                <TableHead className="text-left">Supplier</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {filteredMaterials.map((rm: RawMaterial) => {
                                                const isSelected = isMaterialSelected(rm.id);
                                                
                                                return (
                                                  <TableRow 
                                                    key={rm.id} 
                                                    className={`hover:bg-primary-50/50 border-primary-100 cursor-pointer ${
                                                      isSelected ? 'bg-primary-100/30 border-primary-300' : ''
                                                    }`}
                                                    onClick={isNewMode ? () => handleMaterialSelect(rm, selectedBomDetail.id) : undefined}
                                                  >
                                                    {isNewMode && (
                                                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                          type={isBopLine ? 'checkbox' : 'radio'}
                                                          name={isBopLine ? undefined : `material-${processId}-${selectedBomDetail.id}`}
                                                          checked={isSelected}
                                                          onChange={() => handleMaterialSelect(rm, selectedBomDetail.id)}
                                                          className="h-4 w-4 text-primary-600 border-primary-300 focus:ring-primary-500"
                                                        />
                                                      </TableCell>
                                                    )}
                                                    {!rm.isWIP && <TableCell className="text-left font-medium text-blue-600">{rm.grnNumber}</TableCell>}
                                                    <TableCell className="text-center">{rm.heatNo}</TableCell>
                                                    <TableCell className="text-center">{rm.receivedQuantity}</TableCell>
                                                    <TableCell className="text-center">{rm.remainingNop ?? rm.nop ?? 'N/A'}</TableCell>
                                                    <TableCell className="text-center font-medium text-green-600">
                                                      {rm.remainingQuantity}
                                                    </TableCell>
                                                    <TableCell className="text-center">{rm.unit}</TableCell>
                                                    {!rm.isWIP && <TableCell className="text-center text-sm">{rm.receivedDate}</TableCell>}
                                                    <TableCell className="text-left text-sm">{rm.supplierName}</TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      ) : (
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                          <Info className="h-8 w-8 text-gray-400 mb-2" />
                                          <p className="text-gray-600 text-sm">No materials available for this BOM detail</p>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()
                              ) : (
                                <div className="flex flex-col items-center justify-center py-8 text-center bg-gray-50 rounded-lg">
                                  <Info className="h-8 w-8 text-gray-400 mb-2" />
                                  <p className="text-gray-600 text-sm">Please select a BOM detail (priority) above to view materials</p>
                                </div>
                              )}
                            </>
                          ) : (
                            /* Case 2: Same priority (or single priority) → show all BOM details in separate tables */
                            (() => {
                              // Group by priority (should be same priority, but handle multiple BOM details)
                              const priorityGroups = new Map<string | number, BomDetail[]>();
                              bomDetails.forEach(bd => {
                                const priority = bd.priority ?? 'null';
                                if (!priorityGroups.has(priority)) {
                                  priorityGroups.set(priority, []);
                                }
                                priorityGroups.get(priority)!.push(bd);
                              });
                              
                              return (
                                <div className="space-y-6">
                                  {Array.from(priorityGroups.entries()).sort(([priorityA], [priorityB]) => {
                                    const priA = priorityA === 'null' ? 999 : Number(priorityA);
                                    const priB = priorityB === 'null' ? 999 : Number(priorityB);
                                    return priA - priB;
                                  }).map(([priority, priorityBomDetails]) => {
                                    // If multiple BOM details with same priority, each gets its own table
                                    if (priorityBomDetails.length > 1) {
                                      return priorityBomDetails.map((bomDetail) => {
                                        const materials = materialsByBomDetail.get(bomDetail.id) || [];
                                        const filteredMaterials = materials.filter(rm => {
                                    const remainingQty = rm.remainingQuantity ?? 0;
                                    return remainingQty > 0 ;
                                  });
                                        const isLoading = loadingMaterialsByBomDetail.get(bomDetail.id) || false;
                                        const categoryName = bomDetail.rawCategory?.categoryName || bomDetail.rawItem?.itemCategory?.categoryName;
                                        const isInternal = isInternalManufacturingParts(categoryName);
                                        const itemName = isInternal 
                                          ? (bomDetail.endgood?.endgoodName || 'Unknown')
                                          : (bomDetail.rawItem?.itemName || 'Unknown');
                                        const isBopLine = isBopBomLine(bomDetail);
                                        
                                        return (
                                          <div key={bomDetail.id} className="space-y-2">
                                            <div className="mb-2 text-sm text-gray-600">
                                              <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs mr-2">
                                                {itemName}
                                              </Badge>
                                              {priority !== 'null' && (
                                                <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs">
                                                  Priority: {priority} | BOM Qty (for 1 part): {bomDetail.quantity} {bomDetail.unit}
                                                </Badge>
                                              )}
                                            </div>
                                            
                                            {isLoading ? (
                                              <div className="flex flex-col items-center justify-center py-8">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                                                <p className="text-gray-600 text-sm">Loading materials...</p>
                                              </div>
                                            ) : filteredMaterials.length > 0 ? (
                                              <div className="rounded-lg border border-primary-200 overflow-hidden">
                                                <Table>
                                                      <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                                                        <TableRow className="border-primary-200">
                                                          {isNewMode && <TableHead className="w-12 text-center">Select</TableHead>}
                                                          {!filteredMaterials[0]?.isWIP && <TableHead className="text-left">GRN Number</TableHead>}
                                                          <TableHead className="text-center">
                                                            {filteredMaterials[0]?.isWIP ? 'Lot No. (WO)' : 'Heat No.'}
                                                          </TableHead>
                                                          <TableHead className="text-center">Received Qty</TableHead>
                                                          <TableHead className="text-center">Remaining NOP</TableHead>
                                                          <TableHead className="text-center">Remaining Qty</TableHead>
                                                          <TableHead className="text-center">Unit</TableHead>
                                                          {!filteredMaterials[0]?.isWIP && <TableHead className="text-center">Received Date</TableHead>}
                                                          <TableHead className="text-left">Supplier</TableHead>
                                                        </TableRow>
                                                      </TableHeader>
                                                      <TableBody>
                                                        {filteredMaterials.map((rm: RawMaterial) => {
                                                          const isSelected = isMaterialSelected(rm.id);
                                                          
                                                          return (
                                                            <TableRow 
                                                              key={rm.id} 
                                                              className={`hover:bg-primary-50/50 border-primary-100 cursor-pointer ${
                                                                isSelected ? 'bg-primary-100/30 border-primary-300' : ''
                                                              }`}
                                                              onClick={isNewMode ? () => handleMaterialSelect(rm, bomDetail.id) : undefined}
                                                            >
                                                              {isNewMode && (
                                                                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                                                  <input
                                                                    type={isBopLine ? 'checkbox' : 'radio'}
                                                                    name={isBopLine ? undefined : `material-${processId}-${bomDetail.id}`}
                                                                    checked={isSelected}
                                                                    onChange={() => handleMaterialSelect(rm, bomDetail.id)}
                                                                    className="h-4 w-4 text-primary-600 border-primary-300 focus:ring-primary-500"
                                                                  />
                                                                </TableCell>
                                                              )}
                                                              {!rm.isWIP && <TableCell className="text-left font-medium text-blue-600">{rm.grnNumber}</TableCell>}
                                                              <TableCell className="text-center">{rm.heatNo}</TableCell>
                                                              <TableCell className="text-center">{rm.receivedQuantity}</TableCell>
                                                              <TableCell className="text-center">{rm.remainingNop ?? rm.nop ?? 'N/A'}</TableCell>
                                                              <TableCell className="text-center font-medium text-green-600">
                                                                {rm.remainingQuantity}
                                                              </TableCell>
                                                              <TableCell className="text-center">{rm.unit}</TableCell>
                                                              {!rm.isWIP && <TableCell className="text-center text-sm">{rm.receivedDate}</TableCell>}
                                                              <TableCell className="text-left text-sm">{rm.supplierName}</TableCell>
                                                            </TableRow>
                                                          );
                                                        })}
                                                      </TableBody>
                                                </Table>
                                              </div>
                                            ) : (
                                              <div className="flex flex-col items-center justify-center py-8 text-center">
                                                <Info className="h-8 w-8 text-gray-400 mb-2" />
                                                <p className="text-gray-600 text-sm">No materials available for this BOM detail</p>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      });
                                    } else {
                                      // Single BOM detail with this priority
                                      const bomDetail = priorityBomDetails[0];
                                      const materials = materialsByBomDetail.get(bomDetail.id) || [];
                                      const filteredMaterials = materials.filter(rm => {
                                    const remainingQty = rm.remainingQuantity ?? 0;
                                    return remainingQty > 0;
                                  });
                                      const isLoading = loadingMaterialsByBomDetail.get(bomDetail.id) || false;
                                      const categoryName = bomDetail.rawCategory?.categoryName || bomDetail.rawItem?.itemCategory?.categoryName;
                                      const isInternal = isInternalManufacturingParts(categoryName);
                                      const itemName = isInternal 
                                        ? (bomDetail.endgood?.endgoodName || 'Unknown')
                                        : (bomDetail.rawItem?.itemName || 'Unknown');
                                      const isBopLine = isBopBomLine(bomDetail);
                                      
                                      return (
                                        <div key={bomDetail.id} className="space-y-2">
                                          <div className="mb-2 text-sm text-gray-600">
                                            <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs mr-2">
                                              {itemName}
                                            </Badge>
                                            {priority !== 'null' && (
                                              <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs">
                                                Priority: {priority} | BOM Qty (for 1 part): {bomDetail.quantity} {bomDetail.unit}
                                              </Badge>
                                            )}
                                          </div>
                                          
                                          {isLoading ? (
                                            <div className="flex flex-col items-center justify-center py-8">
                                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                                              <p className="text-gray-600 text-sm">Loading materials...</p>
                                            </div>
                                          ) : filteredMaterials.length > 0 ? (
                                            <div className="rounded-lg border border-primary-200 overflow-hidden">
                                              <Table>
                                                    <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                                                      <TableRow className="border-primary-200">
                                                        {isNewMode && <TableHead className="w-12 text-center">Select</TableHead>}
                                                        {!filteredMaterials[0]?.isWIP && <TableHead className="text-left">GRN Number</TableHead>}
                                                        <TableHead className="text-center">
                                                          {filteredMaterials[0]?.isWIP ? 'Lot No. (WO)' : 'Heat No.'}
                                                        </TableHead>
                                                        <TableHead className="text-center">Received Qty</TableHead>
                                                        <TableHead className="text-center">Remaining NOP</TableHead>
                                                        <TableHead className="text-center">Remaining Qty</TableHead>
                                                        <TableHead className="text-center">Unit</TableHead>
                                                        {!filteredMaterials[0]?.isWIP && <TableHead className="text-center">Received Date</TableHead>}
                                                        <TableHead className="text-left">Supplier</TableHead>
                                                      </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                      {filteredMaterials.map((rm: RawMaterial) => {
                                                        const isSelected = isMaterialSelected(rm.id);
                                                        
                                                        return (
                                                          <TableRow 
                                                            key={rm.id} 
                                                            className={`hover:bg-primary-50/50 border-primary-100 cursor-pointer ${
                                                              isSelected ? 'bg-primary-100/30 border-primary-300' : ''
                                                            }`}
                                                            onClick={isNewMode ? () => handleMaterialSelect(rm, bomDetail.id) : undefined}
                                                          >
                                                            {isNewMode && (
                                                              <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                  type={isBopLine ? 'checkbox' : 'radio'}
                                                                  name={isBopLine ? undefined : `material-${processId}-${bomDetail.id}`}
                                                                  checked={isSelected}
                                                                  onChange={() => handleMaterialSelect(rm, bomDetail.id)}
                                                                  className="h-4 w-4 text-primary-600 border-primary-300 focus:ring-primary-500"
                                                                />
                                                              </TableCell>
                                                            )}
                                                            {!rm.isWIP && <TableCell className="text-left font-medium text-blue-600">{rm.grnNumber}</TableCell>}
                                                            <TableCell className="text-center">{rm.heatNo}</TableCell>
                                                            <TableCell className="text-center">{rm.receivedQuantity}</TableCell>
                                                            <TableCell className="text-center">{rm.remainingNop ?? rm.nop ?? 'N/A'}</TableCell>
                                                            <TableCell className="text-center font-medium text-green-600">
                                                              {rm.remainingQuantity}
                                                            </TableCell>
                                                            <TableCell className="text-center">{rm.unit}</TableCell>
                                                            {!rm.isWIP && <TableCell className="text-center text-sm">{rm.receivedDate}</TableCell>}
                                                            <TableCell className="text-left text-sm">{rm.supplierName}</TableCell>
                                                          </TableRow>
                                                        );
                                                      })}
                                                    </TableBody>
                                              </Table>
                                            </div>
                                          ) : (
                                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                              <Info className="h-8 w-8 text-gray-400 mb-2" />
                                              <p className="text-gray-600 text-sm">No materials available for this BOM detail</p>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                  })}
                                </div>
                              );
                            })()
                          )}
                        </CardContent>
                      </Card>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4">
                  <Info className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="font-medium text-primary-800 mb-2">No Available Raw Materials</h3>
                <p className="text-gray-600 max-w-md">
                  No raw materials found in GRN details for the selected part. Please ensure the part has a valid BOM and materials have been received.
                </p>
              </div>
            )
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4">
                <Info className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="font-medium text-primary-800 mb-2">Select Prerequisites</h3>
              <p className="text-gray-600 max-w-md">
                Please select a <strong>Sales Order</strong>, <strong>Part Name</strong>, and <strong>BOM</strong> above to view available raw materials for issuance.
              </p>
              <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${selectedSalesOrder ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span>Sales Order</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${selectedPart ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span>Part Name</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${currentBomHeader ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span>BOM Selection</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Endgood Variant Bulk Input - Only show when endgood is selected and has variants */}
      {isNewMode && selectedPart && availableVariants.length > 0 && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Bulk Variant Issuance
            </CardTitle>
            <CardDescription>
              Specify issue quantities for each FG variant. FG quantity is calculated automatically based on BOM.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {loadingVariants ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mr-3"></div>
                <span className="text-gray-600">Loading variants...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg border border-primary-200 overflow-hidden">
                  <Table>
                    <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableRow className="border-primary-200">
                        <TableHead className="text-center w-16">Thumbnail</TableHead>
                        <TableHead className="text-left">FG Variant</TableHead>
                        <TableHead className="text-center">Issue Qty</TableHead>
                        <TableHead className="text-center">FG Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {variantEntries.map((entry) => (
                        <TableRow key={entry.variantId} className="hover:bg-primary-50/50">
                          <TableCell className="text-center">
                            <VariantThumbnail 
                              variantName={entry.variant.color} 
                              endgoodId={selectedPart || undefined} 
                            />
                          </TableCell>
                          <TableCell className="text-left">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-6 h-6 rounded-full border-2 border-gray-300"
                                style={{ backgroundColor: entry.variant.color?.toLowerCase() || '#cccccc' }}
                                title={entry.variant.color}
                              />
                              <div>
                                <p className="font-medium text-gray-900">{entry.variant.color}</p>
                                <p className="text-sm text-gray-500">Variant #{entry.variant.id}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Input
                                type="number"
                                value={entry.issueQty}
                                onChange={(e) => handleVariantIssueQtyChange(entry.variantId, e.target.value)}
                                placeholder="0"
                                className="w-24 text-center border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center">
                              <Badge 
                                variant="outline" 
                                className={`border-2 px-3 py-1 ${
                                  entry.fgQty > 0 
                                    ? 'border-green-300 bg-green-50 text-green-700' 
                                    : 'border-gray-300 bg-gray-50 text-gray-500'
                                }`}
                              >
                                {entry.fgQty}
                              </Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-800">
                    <FileText className="h-5 w-5 text-primary-700" />
                    <span className="text-sm">
                      <strong>FG Quantity Calculation:</strong> Based on BOM requirements. 
                      Total raw material per part: {bomDetails.reduce((total, detail) => total + detail.quantity, 0).toFixed(2)} units
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Issuance Details - Only show in new mode */}
      {isNewMode && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            Issuance Details
          </CardTitle>
          <CardDescription>
            <strong>BOP</strong> lines: enter <strong>finished parts</strong>; material issue sent to the server is parts × BOM qty per line.{' '}
            <strong>Raw material</strong> lines: enter issue quantity per line (unchanged; not multiplied).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="issueDate">Issue Date *</Label>
              <Input
                id="issueDate"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="issueQty">Issue quantity / parts *</Label>
              <Input
                id="issueQty"
                type="text"
                placeholder="BOP: parts · Raw material: qty per line"
                value={issueQty}
                onChange={(e) => handleIssueQtyChange(e.target.value)}
                className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${
                  isIssueQuantityExceeded() ? 'border-red-500 focus:border-red-500' : ''
                }`}
                disabled={processSeq1BomDetails.length === 0 || processSeq1BomDetails.every(bd => !hasSelectedMaterialForBomDetail(bd.id))}
              />
              {processSeq1BomDetails.length > 0 && selectedMaterials.size > 0 && (
                <p className="text-xs text-gray-500">
                  Max allowed input: {getMaxAllowedIssueQuantity() === Infinity ? '—' : getMaxAllowedIssueQuantity()} (BOP: combined remaining across selected heats; raw material by remaining qty).
                </p>
              )}
              {processSeq1BomDetails.length > 0 && processSeq1BomDetails.every(bd => !hasSelectedMaterialForBomDetail(bd.id)) && (
                <p className="text-xs text-gray-500">Please select materials first</p>
              )}
              {isIssueQuantityExceeded() && issueQty && parseFloat(issueQty) > 0 && (
                <div className="space-y-1">
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Calculated quantities exceed available for some materials
                  </p>
                  {processSeq1BomDetails.map((bomDetail) => {
                    const selectedMaterialsForLine = getSelectedMaterialsForBomDetail(bomDetail.id);
                    if (selectedMaterialsForLine.length === 0) return null;

                    const issueQtyNum = parseFloat(issueQty);
                    const materialIssue = issueAmountForApi(bomDetail, issueQtyNum);
                    const available = isBopBomLine(bomDetail)
                      ? selectedMaterialsForLine.reduce((sum, m) => sum + m.remainingQuantity, 0)
                      : selectedMaterialsForLine[0].remainingQuantity;

                    if (materialIssue <= available) return null;

                    const categoryName = getBomLineCategoryName(bomDetail);
                    const isInternal = isInternalManufacturingParts(categoryName);
                    const itemName = isInternal 
                      ? (bomDetail.endgood?.endgoodName || 'Unknown')
                      : (bomDetail.rawItem?.itemName || 'Unknown');
                    
                    return (
                      <p key={bomDetail.id} className="text-xs text-red-600 ml-5">
                        {itemName}: need {materialIssue.toFixed(6)} exceeds combined remaining {available}
                        {isBopBomLine(bomDetail) && selectedMaterialsForLine.length > 1
                          ? ` across ${selectedMaterialsForLine.length} heats`
                          : ` ${selectedMaterialsForLine[0].unit}`}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="partsCanCreate">Parts That Can Be Created</Label>
              <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-800">{calculatedParts} parts</span>
              </div>
              {parseFloat(issueQty) > 0 && !isNaN(parseFloat(issueQty)) && calculatedParts > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium">How parts are calculated</p>
                  <p className="text-xs text-blue-600">
                    <strong>BOP:</strong> input is parts (material issued = parts × BOM qty to API).{' '}
                    <strong>Raw material:</strong> ⌊issue qty ÷ BOM per part⌋. Work order parts = minimum across selected lines →{' '}
                    <strong>{calculatedParts}</strong>.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Calculated Material Quantities */}
          {processSeq1BomDetails.length > 0 && issueQty && parseFloat(issueQty) > 0 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-primary-800">Calculated Material Quantities</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {processSeq1BomDetails.map((bomDetail) => {
                  const selectedMaterialsForLine = getSelectedMaterialsForBomDetail(bomDetail.id);
                  const categoryName = getBomLineCategoryName(bomDetail);
                  const isInternal = isInternalManufacturingParts(categoryName);
                  const itemName = isInternal 
                    ? (bomDetail.endgood?.endgoodName || 'Unknown')
                    : (bomDetail.rawItem?.itemName || 'Unknown');
                  
                  if (selectedMaterialsForLine.length === 0) {
                    return null;
                  }

                  const issueQtyNum = parseFloat(issueQty);
                  const bop = isBopBomLine(bomDetail);
                  const partsFromLine = partsFromIssueInputForLine(bomDetail, issueQtyNum);
                  const materialToIssue = issueAmountForApi(bomDetail, issueQtyNum);
                  const combinedAvailable = selectedMaterialsForLine.reduce(
                    (sum, m) => sum + m.remainingQuantity,
                    0,
                  );
                  const exceedsLimit = materialToIssue > combinedAvailable;
                  const allocations = bop
                    ? allocateBopIssueAcrossHeats(bomDetail, issueQtyNum, selectedMaterialsForLine)
                    : [{ material: selectedMaterialsForLine[0], calculatedQty: materialToIssue }];
                  const unit = bomDetail.unit || selectedMaterialsForLine[0].unit;

                  return (
                    <div key={bomDetail.id} className="space-y-2 p-4 border border-blue-200 rounded-lg bg-blue-50/30">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-blue-800">
                          {itemName}
                          {bop && (
                            <span className="ml-2 text-xs font-normal text-blue-600">(BOP)</span>
                          )}
                          {!bop && (
                            <span className="ml-2 text-xs font-normal text-gray-500">(Raw material)</span>
                          )}
                        </Label>
                        <Badge variant="outline" className="border-blue-300 text-blue-700">
                          {partsFromLine} parts (this line)
                        </Badge>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">
                            Available:{' '}
                            <span className="font-medium text-green-600">
                              {combinedAvailable} {unit}
                              {bop && selectedMaterialsForLine.length > 1
                                ? ` (${selectedMaterialsForLine.length} heats)`
                                : ''}
                            </span>
                          </span>
                          {exceedsLimit && (
                            <span className="text-red-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Exceeds available
                            </span>
                          )}
                        </div>
                        <p className="text-blue-600">
                          {bop ? (
                            <>
                              Total API issue: {issueQtyNum} × {bomDetail.quantity} = {materialToIssue.toFixed(6)} {unit}
                            </>
                          ) : (
                            <>
                              Formula: {issueQty} ÷ {bomDetail.quantity} = {partsFromLine} parts · Issued: {issueQtyNum.toFixed(6)} {unit}
                            </>
                          )}
                        </p>
                        {bop && allocations.length > 0 && (
                          <div className="mt-2 space-y-1 rounded border border-blue-100 bg-white/60 p-2">
                            <p className="font-medium text-blue-700">Per heat allocation</p>
                            {allocations.map(({ material, calculatedQty }) => (
                              <p key={material.id} className="text-blue-600">
                                Heat {material.heatNo}: {calculatedQty.toFixed(6)} {unit}
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="text-gray-500">
                          Required per part: {bomDetail.quantity} {unit}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Material Issues History - Show in view/edit mode */}
      {(isViewMode || isEditMode) && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Material Issues History
            </CardTitle>
            <CardDescription>
              {variantBreakdowns.length > 0 
                ? 'Material issued per variant breakdown' 
                : 'Previously issued materials for this work order'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {loadingMaterialIssues ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4"></div>
                <p className="text-gray-600">Loading material issues...</p>
              </div>
            ) : materialIssues.length > 0 ? (
              <div className="space-y-6">
                {/* Material Issues History Table - Always shown */}
                <div className="rounded-lg border border-primary-200 overflow-hidden">
                  <Table>
                    <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableRow className="border-primary-200">
                        <TableHead className="text-left w-32">Issue Date</TableHead>
                        <TableHead className="text-left w-32">Raw Material</TableHead>
                        <TableHead className="text-center w-20">Issue Qty</TableHead>
                        <TableHead className="text-center w-24">Consumed Qty</TableHead>
                        <TableHead className="text-center w-20">Status</TableHead>
                        <TableHead className="text-left w-24">Heat Number</TableHead>
                        <TableHead className="text-left w-24">Issued By</TableHead>
                          {!isViewMode && (
                            <TableHead className="text-center w-20">Actions</TableHead>
                          )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {materialIssues.map((issue) => (
                        <TableRow key={issue.id} className="hover:bg-primary-50/50 border-primary-100">
                          <TableCell className="w-32">
                            {editingIssueId === issue.id ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className="w-28 justify-start text-left font-normal border-primary-200 focus:border-primary-400 focus:ring-primary-400 text-xs h-8"
                                  >
                                    {editingIssueDate ? new Date(editingIssueDate).toLocaleDateString() : "Select date"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start" side="bottom">
                                  <Calendar
                                    mode="single"
                                    selected={editingIssueDate ? new Date(editingIssueDate) : undefined}
                                    onSelect={(date) => setEditingIssueDate(date ? date.toISOString().split('T')[0] : '')}
                                    initialFocus
                                    className="scale-90"
                                  />
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-sm">{new Date(issue.issueDate).toLocaleDateString()}</span>
                            )}
                          </TableCell>
                          <TableCell className="font-medium text-sm">{issue.rawItemSku || 'N/A'}</TableCell>
                          <TableCell className="text-center">
                            {editingIssueId === issue.id ? (
                              <Input
                                type="text"
                                value={editingIssueQty}
                                onChange={(e) => setEditingIssueQty(e.target.value)}
                                className="w-20 border-primary-200 focus:border-primary-400 focus:ring-primary-400 text-center"
                              />
                            ) : (!isViewMode && variantBreakdowns.length === 0) ? (
                              <span 
                                className="text-sm cursor-pointer hover:text-primary-600" 
                                onClick={() => handleEditIssue(issue)}
                              >
                                {issue.issueQuantity}
                              </span>
                            ) : (
                              <span className="text-sm">{issue.issueQuantity}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center text-sm">{issue.consumedQuantity}</TableCell>
                          <TableCell className="text-center">
                            <Badge 
                              variant={issue.status === 'ISSUED' ? 'default' : 'secondary'}
                              className={issue.status === 'ISSUED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                            >
                              {issue.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{issue.heatNumber || 'N/A'}</TableCell>
                          <TableCell className="text-sm">{issue.issuedBy || 'N/A'}</TableCell>
                          {!isViewMode && (
                            <TableCell className="text-center">
                              {editingIssueId === issue.id ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSaveEdit(issue)}
                                    disabled={updatingIssue}
                                    className="text-green-600 hover:bg-green-50 border-green-200"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCancelEdit}
                                    disabled={updatingIssue}
                                    className="text-red-600 hover:bg-red-50 border-red-200"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : variantBreakdowns.length === 0 ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditIssue(issue)}
                                  className="text-green-600 hover:bg-green-50 border-green-200"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              ) : (
                                <span className="text-gray-400 text-xs">View Only</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Variant Breakdown Table - Only shown when variants exist */}
                {variantBreakdowns.length > 0 && (
                  <div className="space-y-4">
                    <div className="border-t border-primary-200 pt-4">
                      <h4 className="text-lg font-semibold text-primary-800 mb-4">Material Issued per Variant</h4>
                      <div className="rounded-lg border border-primary-200 overflow-hidden">
                        <Table>
                          <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                            <TableRow className="border-primary-200">
                              <TableHead className="text-center w-16">Thumbnail</TableHead>
                              <TableHead className="text-left w-32">Variant Color</TableHead>
                              <TableHead className="text-center w-32">Material Issued</TableHead>
                              <TableHead className="text-center w-20">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {variantBreakdowns.map((variant) => (
                              <TableRow key={variant.id} className="hover:bg-primary-50/50 border-primary-100">
                                <TableCell className="text-center">
                                  <VariantThumbnail 
                                    variantName={variant.endgoodVariantColor} 
                                    endgoodId={workOrder?.endgood?.id || undefined} 
                                  />
                                </TableCell>
                                <TableCell className="font-medium text-sm">
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-4 h-4 rounded-full border border-gray-300"
                                      style={{ backgroundColor: variant.endgoodVariantColor.toLowerCase() }}
                                      title={variant.endgoodVariantColor}
                                    />
                                    {variant.endgoodVariantColor}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  {editingVariantId === variant.id ? (
                                    <Input
                                      type="number"
                                      value={editingVariantMaterialIssued}
                                      onChange={(e) => setEditingVariantMaterialIssued(e.target.value)}
                                      className="w-24 border-primary-200 focus:border-primary-400 focus:ring-primary-400 text-center"
                                      step="0.01"
                                      min="0"
                                    />
                                  ) : (
                                    <span 
                                      className="text-sm cursor-pointer hover:text-primary-600 font-medium"
                                      onClick={() => handleEditVariantMaterialIssued(variant)}
                                    >
                                      {variant.materialIssued}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {editingVariantId === variant.id ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleSaveVariantEdit(variant)}
                                        disabled={updatingVariant}
                                        className="text-green-600 hover:bg-green-50 border-green-200"
                                      >
                                        <Save className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleCancelVariantEdit}
                                        disabled={updatingVariant}
                                        className="text-red-600 hover:bg-red-50 border-red-200"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditVariantMaterialIssued(variant)}
                                      className="text-green-600 hover:bg-green-50 border-green-200"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4">
                  <Info className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="font-medium text-primary-800 mb-2">No Material Issues Found</h3>
                <p className="text-gray-600 max-w-md">
                  No material issues have been created for this work order yet.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Work Order Operations - Show in view/edit mode */}
      {(isViewMode || isEditMode) && (
        <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Work Order Operations
            </CardTitle>
            <CardDescription>
              Production operations and their progress for this work order
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {loadingOperations ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4"></div>
                <p className="text-gray-600">Loading work order operations...</p>
              </div>
            ) : workOrderOperations.length > 0 ? (
              <div className="rounded-lg border border-primary-200 overflow-hidden">
                <Table>
                  <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableRow className="border-primary-200">
                      <TableHead className="text-left w-20">Operation</TableHead>
                      <TableHead className="text-left w-32">Operation Name</TableHead>
                      <TableHead className="text-center w-24">Machine</TableHead>
                      <TableHead className="text-center w-24">Operator</TableHead>
                      <TableHead className="text-center w-24">Production Qty</TableHead>
                      <TableHead className="text-center w-24">Reject Qty</TableHead>
                      <TableHead className="text-center w-24">Production Date</TableHead>
                      {isEditMode && <TableHead className="text-center w-20">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workOrderOperations.map((operation) => (
                      <TableRow key={operation.id} className="hover:bg-primary-50/50 border-primary-100">
                        <TableCell className="font-medium text-blue-600 text-sm">{operation.operationNumber}</TableCell>
                        <TableCell className="font-medium text-sm">{operation.operationName}</TableCell>
                        <TableCell className="text-center text-sm">{operation.machineCode || 'N/A'}</TableCell>
                        <TableCell className="text-center text-sm">{operation.assignedOperator || 'N/A'}</TableCell>
                        <TableCell className="text-center">
                          {editingOperationId === operation.id ? (
                            <Input
                              type="text"
                              value={editingProductionQty}
                              onChange={(e) => setEditingProductionQty(e.target.value)}
                              className="w-20 border-primary-200 focus:border-primary-400 focus:ring-primary-400 text-center text-sm"
                            />
                          ) : (
                            <span className="text-sm">{operation.quantityCompleted}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {editingOperationId === operation.id ? (
                            <Input
                              type="text"
                              value={editingRejectQty}
                              onChange={(e) => setEditingRejectQty(e.target.value)}
                              className="w-20 border-primary-200 focus:border-primary-400 focus:ring-primary-400 text-center text-sm"
                            />
                          ) : (
                            <span className="text-sm">{operation.quantityRejected}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {operation.productionDate ? new Date(operation.productionDate).toLocaleDateString() : 
                           operation.startTime ? new Date(operation.startTime).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        {isEditMode && (
                          <TableCell className="text-center">
                            {editingOperationId === operation.id ? (
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSaveEditOperation(operation)}
                                  disabled={updatingOperation}
                                  className="text-green-600 hover:bg-green-50 border-green-200"
                                >
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEditOperation}
                                  disabled={updatingOperation}
                                  className="text-red-600 hover:bg-red-50 border-red-200"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditOperation(operation)}
                                className="text-green-600 hover:bg-green-50 border-green-200"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4">
                  <Info className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="font-medium text-primary-800 mb-2">No Work Order Operations Found</h3>
                <p className="text-gray-600 max-w-md">
                  No work order operations have been created for this work order yet.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {!isViewMode && (
        <div className="flex justify-end gap-4">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving || isIssueQuantityExceeded()}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Creating Material Issue...' : 'Save Work Order'}
          </Button>
        </div>
      )}
      
      {/* View Mode Actions */}
      {isViewMode && (
        <div className="flex justify-end gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/work-orders')}
            className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
          >
            Back to Work Orders
          </Button>
          
        </div>
      )}
    </div>
  );
}

// Component to display variant thumbnails
const VariantThumbnail: React.FC<{ variantName: string; endgoodId?: number }> = ({ variantName, endgoodId }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!endgoodId || !variantName) return;

    const loadThumbnail = async () => {
      setLoading(true);
      try {
        // Get all files for the endgood
        const files = await fileUploadApiService.getFilesByEndgood(endgoodId);
        
        // Find file that matches variant name (case-insensitive)
        const matchingFile = files.find(file => {
          const fileName = file.originalName || file.originalFileName || file.fileName || '';
          return fileName.toLowerCase().includes(variantName.toLowerCase());
        });

        if (matchingFile?.fileUrl) {
          setThumbnailUrl(matchingFile.fileUrl);
        }
      } catch (error) {
        // console.error('Error loading variant thumbnail:', error);
      } finally {
        setLoading(false);
      }
    };

    loadThumbnail();
  }, [variantName, endgoodId]);

  if (loading) {
    return (
      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  if (!thumbnailUrl) {
    return (
      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
        <Package className="h-4 w-4 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={thumbnailUrl}
      alt={`${variantName} thumbnail`}
      className="w-8 h-8 object-cover rounded border border-gray-200"
      style={{
        width: '32px',
        height: '32px',
        objectFit: 'cover'
      }}
      onError={(e) => {
        // Fallback to icon if image fails to load
        const target = e.currentTarget as HTMLImageElement;
        target.style.display = 'none';
        const fallback = target.nextElementSibling as HTMLElement;
        if (fallback) {
          fallback.style.display = 'flex';
        }
      }}
    />
  );
};